import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import Prism from "prismjs";
import "prismjs/components/prism-markup";
import "prismjs/themes/prism-tomorrow.css";
import { accessLogRetentionDayOptions, formatAccessLogRetentionDaysLabel } from "../accessLogs";
import { archiveRetentionMonthOptions, formatArchiveRetentionMonthsLabel } from "../archiving";
import { apiKeyExpiryDayOptions, formatApiKeyExpiryDaysLabel } from "../apiKeys";
import { lockBodyScroll, unlockBodyScroll } from "../app/bodyScrollLock";
import { defaultTicketUpdateEmailTemplateHtml, defaultTicketUpdateEmailTemplateSubject, normalizeOutboundTemplateHtml, normalizeOutboundTemplateSubject, renderEmailTemplate, ticketUpdateEmailPlaceholderTokens } from "../app/emailTemplates";
import { languageNames, logsViewCopy, settingsViewCopy } from "../app/i18n";
import type { AppLanguage } from "../app/language";
import type {
  AppBranding,
  CaseEmailRoute,
  CaseEmailRouteOutboundVerificationStatus,
  ColorMode,
  KnowledgeArticle,
  ProductPlan,
  ProductPlanCategory,
  ProductPlanPrice,
  ProductPlanPriceInterval,
  ProductPlanStatus,
  PlanLimits,
  SettingsSection,
  Ticket,
  TicketCategory,
  TicketCategoryCustomField,
  TicketCategoryCustomFieldType,
  WorkspaceSettings,
  WorkspaceNotificationSettings,
} from "../app/types";
import { AnimatedModal } from "../app/ui";
import { createRecordId, formatDateTimeForInput, normalizeDateTimeForStorage, normalizeTicketCategoryCustomFields, resolveTimeZone } from "../app/utils";
import { settingsSectionPaths } from "../app/data";

function formatLocalizedApiKeyExpiryDaysLabel(days: number, language: AppLanguage) {
  if (language === "ja") {
    if (days === 1) {
      return "1日";
    }

    if (days % 365 === 0) {
      return `${days / 365}年`;
    }

    return `${days}日`;
  }

  return formatApiKeyExpiryDaysLabel(days);
}

function getApiKeyExpiryOptions(language: AppLanguage, noTenantExpiryLabel: string) {
  return [
    { value: "", label: noTenantExpiryLabel },
    ...apiKeyExpiryDayOptions.map(days => ({ value: String(days), label: formatLocalizedApiKeyExpiryDaysLabel(days, language) })),
  ];
}

function getAccessLogRetentionOptions(language: AppLanguage) {
  return [
    { value: "", label: language === "ja" ? "自動削除しない" : "Never auto-delete" },
    ...accessLogRetentionDayOptions.map(days => ({ value: String(days), label: formatAccessLogRetentionDaysLabel(days, language) })),
  ];
}

function getArchiveRetentionOptions(language: AppLanguage, noRetentionLabel: string) {
  return [
    { value: "", label: noRetentionLabel },
    ...archiveRetentionMonthOptions.map(months => ({ value: String(months), label: formatArchiveRetentionMonthsLabel(months, language) })),
  ];
}

const workspaceTimezoneOptions = [
  "UTC",
  "UTC-08 · San Francisco",
  "UTC-05 · New York",
  "UTC+00 · London",
  "UTC+01 · Berlin",
  "UTC+08 · Singapore",
  "UTC+09 · Tokyo",
  "UTC+10 · Sydney",
];

function normalizeWorkspaceTimezone(timezone?: string | null) {
  const normalizedTimezone = timezone?.trim();
  return normalizedTimezone ? normalizedTimezone : null;
}

function getWorkspaceTimezoneOptions(workspaceTimezone: string, selectedTimezone?: string | null) {
  return [...new Set([
    normalizeWorkspaceTimezone(workspaceTimezone),
    normalizeWorkspaceTimezone(selectedTimezone),
    ...workspaceTimezoneOptions,
  ].filter((timezone): timezone is string => Boolean(timezone)))];
}

function formatRestorePointTimestamp(timestamp: string, language: AppLanguage, workspaceTimezone?: string | null) {
  const parsed = new Date(timestamp);

  if (Number.isNaN(parsed.getTime())) {
    return timestamp;
  }

  const resolvedTimeZone = resolveTimeZone(workspaceTimezone);

  return new Intl.DateTimeFormat(language === "ja" ? "ja-JP" : "en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    ...(resolvedTimeZone ? { timeZone: resolvedTimeZone } : {}),
    timeZoneName: "short",
  }).format(parsed);
}

function localizeEmailRoutingSyncMessage(
  message: string,
  language: AppLanguage,
  copy: typeof settingsViewCopy.en.emailIntake.syncMessages,
) {
  if (language === "en") {
    return message;
  }

  switch (message) {
    case settingsViewCopy.en.emailIntake.syncMessages.idle:
      return copy.idle;
    case settingsViewCopy.en.emailIntake.syncMessages.syncing:
      return copy.syncing;
    case settingsViewCopy.en.emailIntake.syncMessages.success:
      return copy.success;
    case settingsViewCopy.en.emailIntake.syncMessages.error:
      return copy.error;
    default:
      return message;
  }
}

type ProductPlanDraft = {
  name: string;
  category: ProductPlanCategory;
  status: ProductPlanStatus;
  description: string;
  price: ProductPlanPrice;
  slaEnabled: boolean;
  slaValue: string;
  slaUnit: "Hours" | "Minutes";
  relatedItemIds: string[];
};

type TicketCategoryDraft = {
  name: string;
  description: string;
  customFields: TicketCategoryCustomField[];
};

type CaseEmailRouteDraft = {
  id: string;
  mailbox: string;
  categoryId: string;
  itemId: string;
  outboundFromEmail: string;
  outboundVerificationStatus: CaseEmailRouteOutboundVerificationStatus;
  outboundVerificationError: string;
  outboundVerifiedAt: string;
  templateSubject: string;
  templateHtml: string;
};

type CaseEmailTemplateEditorDraft = {
  routeId: string;
  templateSubject: string;
  templateHtml: string;
};

type WorkspaceNotificationEventKey = Exclude<keyof WorkspaceNotificationSettings, "timezone">;

const sampleEmailTemplatePreview = {
  actor_name: "Sebmuir",
  recipient_name: "Alex Morgan",
  reply_to_address: "support@example.com",
  ticket_notes: `<div style="margin:0 0 16px;border:1px solid rgba(15,23,42,0.08);border-radius:18px;padding:16px;background:#ffffff;"><div style="white-space:pre-wrap;font-size:15px;line-height:1.7;color:#101828;">We've reviewed the issue and the fix has now been applied to your account.</div></div>`,
  ticket_number: "S-1042",
  ticket_status: "Pending",
  ticket_title: "Login issue after password reset",
  workspace_name: "Kuii Support",
};

function highlightHtmlTemplate(value: string) {
  const markupGrammar = Prism.languages.markup;

  if (!markupGrammar) {
    return value;
  }

  return Prism.highlight(value, markupGrammar, "markup");
}

function HtmlCodeEditor({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (nextValue: string) => void;
  placeholder: string;
}) {
  const [draftValue, setDraftValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const preRef = useRef<HTMLPreElement | null>(null);
  const selectionRef = useRef<{ start: number; end: number } | null>(null);
  const highlightedValue = useMemo(() => highlightHtmlTemplate(draftValue || " "), [draftValue]);

  useEffect(() => {
    if (value !== draftValue) {
      setDraftValue(value);
    }
  }, [draftValue, value]);

  const captureSelection = () => {
    if (!textareaRef.current) {
      return;
    }

    selectionRef.current = {
      start: textareaRef.current.selectionStart,
      end: textareaRef.current.selectionEnd,
    };
  };

  useLayoutEffect(() => {
    if (!textareaRef.current || !selectionRef.current || document.activeElement !== textareaRef.current) {
      return;
    }

    const textarea = textareaRef.current;
    const { start, end } = selectionRef.current;
    textarea.setSelectionRange(start, end);
  }, [draftValue]);

  const syncScroll = () => {
    if (!textareaRef.current || !preRef.current) {
      return;
    }

    const textarea = textareaRef.current;
    const highlighted = preRef.current;
    highlighted.scrollTop = textarea.scrollTop;
    highlighted.scrollLeft = textarea.scrollLeft;
  };

  return (
    <div className="relative overflow-hidden rounded-[18px] border border-[#243041] bg-[#0f172a] shadow-[0_12px_32px_rgba(15,23,42,0.18)]">
      {value ? null : (
        <div className="pointer-events-none absolute left-4 top-4 z-[1] whitespace-pre-wrap font-mono text-[13px] leading-6 text-[#64748b]">
          {placeholder}
        </div>
      )}

      <pre
        ref={preRef}
        aria-hidden="true"
        className="h-[420px] overflow-auto whitespace-pre-wrap break-words px-4 py-4 font-mono text-[13px] leading-6 text-[#e2e8f0]"
        style={{ overflowWrap: "anywhere", scrollbarGutter: "stable" }}
        dangerouslySetInnerHTML={{ __html: highlightedValue }}
      />
      <textarea
        ref={textareaRef}
        value={draftValue}
        onChange={event => {
          const nextValue = event.target.value;
          selectionRef.current = {
            start: event.target.selectionStart,
            end: event.target.selectionEnd,
          };
          setDraftValue(nextValue);
          onChange(nextValue);
        }}
        onClick={captureSelection}
        onScroll={syncScroll}
        onSelect={captureSelection}
        onKeyUp={captureSelection}
        spellCheck={false}
        className="absolute inset-0 h-[420px] w-full resize-none overflow-auto border-none bg-transparent px-4 py-4 font-mono text-[13px] leading-6 text-transparent caret-white outline-none"
        style={{
          WebkitTextFillColor: "transparent",
          overflowWrap: "anywhere",
          scrollbarGutter: "stable",
          whiteSpace: "pre-wrap",
        }}
      />
    </div>
  );
}

function StableTextInput({
  value,
  onChange,
  className,
  placeholder,
  inputMode,
  autoComplete,
}: {
  value: string;
  onChange: (nextValue: string) => void;
  className?: string;
  placeholder?: string;
  inputMode?: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search";
  autoComplete?: string;
}) {
  const [draftValue, setDraftValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectionRef = useRef<{ start: number; end: number } | null>(null);

  useEffect(() => {
    if (value !== draftValue) {
      setDraftValue(value);
    }
  }, [draftValue, value]);

  useLayoutEffect(() => {
    if (!inputRef.current || !selectionRef.current || document.activeElement !== inputRef.current) {
      return;
    }

    const { start, end } = selectionRef.current;
    inputRef.current.setSelectionRange(start, end);
  }, [draftValue]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={draftValue}
      inputMode={inputMode}
      autoComplete={autoComplete}
      placeholder={placeholder}
      className={className}
      onChange={event => {
        const nextValue = event.target.value;
        selectionRef.current = {
          start: event.target.selectionStart ?? nextValue.length,
          end: event.target.selectionEnd ?? nextValue.length,
        };
        setDraftValue(nextValue);
        onChange(nextValue);
      }}
    />
  );
}

function SectionSearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  disabled = false,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label className={className ?? "block w-full sm:w-[24rem] lg:w-[28rem]"}>
      <span className="sr-only">{ariaLabel}</span>
      <div className={`flex items-center gap-2 rounded-2xl border border-[#d0d5dd] bg-white px-3 py-2.5 shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition ${disabled ? "cursor-not-allowed opacity-60" : "focus-within:border-[var(--brand-primary)] focus-within:ring-4 focus-within:ring-[var(--brand-primary-ring)]"}`}>
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 shrink-0 text-[#98a2b3]" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.75 15a6.25 6.25 0 1 0 0-12.5 6.25 6.25 0 0 0 0 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="m13.167 13.167 4.166 4.166" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <input
          type="search"
          value={value}
          disabled={disabled}
          onChange={event => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="w-full bg-transparent text-sm text-[#101828] outline-none placeholder:text-[#98a2b3] disabled:cursor-not-allowed"
        />
      </div>
    </label>
  );
}

function getEmailLocalPart(email: string) {
  return email.trim().toLowerCase().split("@")[0] ?? "";
}

function buildTenantMailboxDomain(tenantHostname: string | null | undefined) {
  const normalizedHostname = tenantHostname?.trim().toLowerCase() ?? "";

  if (!normalizedHostname) {
    return null;
  }

  const segments = normalizedHostname.split(".").filter(Boolean);

  if (segments.length < 3) {
    return null;
  }

  const [tenantSlug, ...rootDomainSegments] = segments;

  if (!tenantSlug || rootDomainSegments.length < 2) {
    return null;
  }

  if (rootDomainSegments[0] === "mail") {
    return normalizedHostname;
  }

  return `${tenantSlug}.mail.${rootDomainSegments.join(".")}`;
}

function buildManagedSupportAddress(mailbox: string, tenantMailboxDomain: string | null | undefined) {
  const localPart = mailbox.trim().toLowerCase();
  const normalizedDomain = tenantMailboxDomain?.trim().toLowerCase() ?? "";

  if (!localPart || !normalizedDomain) {
    return "";
  }

  return `${localPart}@${normalizedDomain}`;
}

function normalizeRouteOutboundFromEmail(value: string | null | undefined) {
  return value?.trim().toLowerCase() ?? "";
}

function isValidEmailAddress(value: string | null | undefined) {
  const normalizedValue = normalizeRouteOutboundFromEmail(value);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedValue);
}

function getRouteOutboundSender(route: Pick<CaseEmailRouteDraft, "mailbox" | "outboundFromEmail">, tenantMailboxDomain: string | null | undefined) {
  return normalizeRouteOutboundFromEmail(route.outboundFromEmail) || buildManagedSupportAddress(route.mailbox, tenantMailboxDomain);
}

const currencyOptions = [
  { label: "USD ($)", code: "USD", symbol: "$" },
  { label: "EUR (€)", code: "EUR", symbol: "€" },
  { label: "GBP (£)", code: "GBP", symbol: "£" },
  { label: "CAD (CA$)", code: "CAD", symbol: "CA$" },
  { label: "JPY (¥)", code: "JPY", symbol: "¥" },
  { label: "AUD (A$)", code: "AUD", symbol: "A$" },
  { label: "NZD (NZ$)", code: "NZD", symbol: "NZ$" },
  { label: "SGD (S$)", code: "SGD", symbol: "S$" },
] as const;

const productPlanCategories: ProductPlanCategory[] = ["Product", "Service", "Subscription"];
const productPlanStatuses: ProductPlanStatus[] = ["Draft", "Public", "Disabled"];
const productPlanPriceIntervals: ProductPlanPriceInterval[] = ["Daily", "Weekly", "Monthly", "Yearly", "One-time"];
const productPlanSlaUnits = ["Hours", "Minutes"] as const;

type SortDirection = "asc" | "desc";
type ProductPlanSortColumn = "name" | "status" | "linked" | "category" | "price";
type TicketCategorySortColumn = "name" | "usage" | "status";

const productPlanStatusClasses: Record<ProductPlanStatus, string> = {
  Draft: "bg-[#f3f4f6] text-[#475467]",
  Public: "bg-[#effaf5] text-[#027a48]",
  Disabled: "bg-[#fef3f2] text-[#b42318]",
};

const productPlanSortHeaders: Array<{ key: ProductPlanSortColumn; label: string }> = [
  { key: "name", label: "Item" },
  { key: "status", label: "Status" },
  { key: "linked", label: "Linked" },
  { key: "category", label: "Type" },
  { key: "price", label: "Cost" },
];

const ticketCategorySortHeaders: Array<{ key: TicketCategorySortColumn; label: string }> = [
  { key: "name", label: "Category" },
  { key: "usage", label: "Usage" },
  { key: "status", label: "Status" },
];

function getEmptyPriceDraft(): ProductPlanPrice {
  return {
    amount: "",
    interval: "Monthly",
  };
}

function getCurrencyMeta(selection: string) {
  return currencyOptions.find(option => option.label === selection) ?? currencyOptions[0];
}

function sanitizePriceAmount(value: string) {
  return value.replace(/[^\d.,-]/g, "").replace(/,/g, "");
}

function hexToRgb(hex: string) {
  const normalized = hex.replace("#", "");
  const safeHex = normalized.length === 3 ? normalized.split("").map(char => `${char}${char}`).join("") : normalized;
  const value = Number.parseInt(safeHex, 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function mixColor(hex: string, amount: number) {
  const { r, g, b } = hexToRgb(hex);
  const target = amount >= 0 ? 255 : 0;
  const intensity = Math.abs(amount);
  const mixChannel = (channel: number) => Math.round(channel + (target - channel) * intensity);

  return `rgb(${mixChannel(r)} ${mixChannel(g)} ${mixChannel(b)})`;
}

function compareText(left: string | null | undefined, right: string | null | undefined) {
  return (left ?? "").localeCompare(right ?? "", undefined, { numeric: true, sensitivity: "base" });
}

function SortIndicatorIcon({ active, direction }: { active: boolean; direction?: SortDirection }) {
  if (!active) {
    return (
      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
        <path d="M3.25 4.25 6 1.5l2.75 2.75" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.25 7.75 6 10.5l2.75-2.75" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return direction === "asc" ? (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.25 4.5 6 1.75 8.75 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 1.75v8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ) : (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.25 7.5 6 10.25 8.75 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 1.75v8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function getSortIndicator(activeColumn: string, activeDirection: SortDirection, column: string) {
  const isActive = activeColumn === column;

  return <SortIndicatorIcon active={isActive} direction={isActive ? activeDirection : undefined} />;
}

function formatPriceAmount(amount: string, currencySelection: string) {
  const normalizedAmount = sanitizePriceAmount(amount).trim();

  if (!normalizedAmount) {
    return "—";
  }

  const numericAmount = Number(normalizedAmount);
  const { code, symbol } = getCurrencyMeta(currencySelection);

  if (!Number.isFinite(numericAmount)) {
    return `${symbol}${normalizedAmount}`;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code,
    minimumFractionDigits: Number.isInteger(numericAmount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

function parseProductPlanSla(sla: string | null | undefined): { enabled: boolean; value: string; unit: "Hours" | "Minutes" } {
  const normalizedSla = sla?.trim() ?? "";

  if (!normalizedSla) {
    return {
      enabled: false,
      value: "",
      unit: "Hours" as const,
    };
  }

  const matchedSla = normalizedSla.match(/(\d+(?:\.\d+)?)\s*(hour|hours|hr|hrs|minute|minutes|min|mins)/i);

  if (!matchedSla) {
    return {
      enabled: true,
      value: normalizedSla.match(/\d+(?:\.\d+)?/)?.[0] ?? "",
      unit: "Hours" as const,
    };
  }

  const [, value = "", unitLabel = ""] = matchedSla;
  const unit: "Hours" | "Minutes" = /min/i.test(unitLabel) ? "Minutes" : "Hours";

  return {
    enabled: true,
    value,
    unit,
  };
}

function formatProductPlanSla(value: string, unit: "Hours" | "Minutes") {
  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return null;
  }

  const numericValue = Number(normalizedValue);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  const normalizedUnit = unit === "Minutes"
    ? numericValue === 1 ? "minute" : "minutes"
    : numericValue === 1 ? "hour" : "hours";

  return `${normalizedValue} ${normalizedUnit}`;
}

function getEmptyProductPlanDraft(): ProductPlanDraft {
  return {
    name: "",
    category: "Subscription",
    status: "Draft",
    description: "",
    price: getEmptyPriceDraft(),
    slaEnabled: false,
    slaValue: "",
    slaUnit: "Hours",
    relatedItemIds: [],
  };
}

function getEmptyTicketCategoryDraft(): TicketCategoryDraft {
  return {
    name: "",
    description: "",
    customFields: [],
  };
}

function getEmptyTicketCategoryFieldDraft(type: TicketCategoryCustomFieldType = "text"): TicketCategoryCustomField {
  return {
    id: createRecordId("ticket-field"),
    label: "",
    type,
    required: false,
    helpText: "",
    options: [],
  };
}

function getTicketCategoryDraft(category: TicketCategory): TicketCategoryDraft {
  return {
    name: category.name,
    description: category.description,
    customFields: normalizeTicketCategoryCustomFields(category.customFields),
  };
}

function getTicketCategoryFromDraft(draft: TicketCategoryDraft, id: string): TicketCategory {
  return {
    id,
    name: draft.name.trim(),
    description: draft.description.trim(),
    customFields: normalizeTicketCategoryCustomFields(draft.customFields),
  };
}

function getCaseEmailRouteDrafts(settings: WorkspaceSettings): CaseEmailRouteDraft[] {
  return settings.caseEmailRoutes.map(route => ({
    id: route.id,
    mailbox: getEmailLocalPart(route.email),
    categoryId: route.categoryId ?? "",
    itemId: route.itemId ?? "",
    outboundFromEmail: normalizeRouteOutboundFromEmail(route.outboundFromEmail),
    outboundVerificationStatus: route.outboundVerificationStatus ?? (route.outboundEnabled ? "pending" : "disabled"),
    outboundVerificationError: route.outboundVerificationError ?? "",
    outboundVerifiedAt: route.outboundVerifiedAt ?? "",
    templateSubject: normalizeOutboundTemplateSubject(route.outboundTemplateSubject) ?? "",
    templateHtml: normalizeOutboundTemplateHtml(route.outboundTemplateHtml) ?? "",
  }));
}

function normalizeWorkspaceNotificationSettingsDraft(settings: WorkspaceNotificationSettings): WorkspaceNotificationSettings {
  const normalizeRule = (rule: WorkspaceNotificationSettings["ticketCreated"]) => ({
    enabled: Boolean(rule.enabled),
    categoryIds: [...new Set(rule.categoryIds)].sort((left, right) => left.localeCompare(right)),
  });

  return {
    timezone: null,
    ticketCreated: normalizeRule(settings.ticketCreated),
    ticketUpdated: normalizeRule(settings.ticketUpdated),
    ticketScheduledReminder: normalizeRule(settings.ticketScheduledReminder),
    ticketSlaWarning: normalizeRule(settings.ticketSlaWarning),
    ticketSlaBreached: normalizeRule(settings.ticketSlaBreached),
  };
}

function getWorkspaceNotificationDraft(settings: WorkspaceSettings): WorkspaceNotificationSettings {
  return normalizeWorkspaceNotificationSettingsDraft(settings.notifications);
}

function countEnabledWorkspaceNotificationRules(settings: WorkspaceNotificationSettings) {
  return Number(settings.ticketCreated.enabled)
    + Number(settings.ticketUpdated.enabled)
    + Number(settings.ticketScheduledReminder.enabled)
    + Number(settings.ticketSlaWarning.enabled)
    + Number(settings.ticketSlaBreached.enabled);
}

function isValidMailboxName(value: string) {
  return /^[a-z0-9](?:[a-z0-9._+-]{0,62}[a-z0-9])?$/i.test(value);
}

function SettingsCard({
  eyebrow,
  title,
  description,
  titleAside,
  actions,
  children,
  className = "",
}: {
  eyebrow: string;
  title: string;
  description: ReactNode;
  titleAside?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)] ${className}`}>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{eyebrow}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{title}</h2>
            {titleAside ?? null}
          </div>
          <p className="mt-2 max-w-2xl text-sm text-[#667085]">{description}</p>
        </div>
        {typeof actions === "string" ? <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{actions}</span> : actions ?? null}
      </div>
      {children}
    </section>
  );
}

function LoadingValueSpinner() {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function BillingSummarySpinner({ className = "h-5 w-5 text-white" }: { className?: string }) {
  return (
    <span className={`inline-flex items-center justify-center ${className}`} aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-full w-full animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function MetricCard({ label, value, detail, isLoading = false }: { label: string; value: string; detail: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{label}</p>
      <div className="mt-4 min-h-[2.25rem]">
        {isLoading ? <LoadingValueSpinner /> : <p className="text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>}
      </div>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

function LoadingSpinnerBadge({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--brand-primary-ring)] bg-white/95 px-3 py-2 text-xs font-semibold text-[var(--brand-primary)] shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>{label}</span>
    </div>
  );
}

function LoadingStateCard({ label }: { label: string }) {
  return (
    <div className="rounded-[22px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-10 text-center">
      <div className="flex justify-center">
        <LoadingSpinnerBadge label={label} />
      </div>
    </div>
  );
}

function CounterBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3.5 w-3.5 animate-spin text-[var(--brand-primary)]" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>{label}</span>
    </span>
  );
}

function getUsageThreshold(baseLimit: number, usage: number, overageBracketSize?: number | null) {
  const normalizedBaseLimit = Math.max(1, baseLimit);

  if (usage <= normalizedBaseLimit) {
    return {
      max: normalizedBaseLimit,
      isOverIncludedQuota: false,
    };
  }

  const normalizedBracketSize = typeof overageBracketSize === "number" && overageBracketSize > 0
    ? overageBracketSize
    : normalizedBaseLimit;

  return {
    max: normalizedBaseLimit + (Math.ceil((usage - normalizedBaseLimit) / normalizedBracketSize) * normalizedBracketSize),
    isOverIncludedQuota: true,
  };
}

function formatCount(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
  return String(value);
}

function formatStorageSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
}

function BillingProgressRow({
  label,
  value,
  max,
  progress,
  barClassName,
  formatValue,
  isOverIncludedQuota = false,
  isLoading = false,
}: {
  label: string;
  value: number;
  max: number;
  progress: number;
  barClassName: string;
  formatValue?: (v: number) => string;
  isOverIncludedQuota?: boolean;
  isLoading?: boolean;
}) {
  const fmt = formatValue ?? String;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-4 text-sm">
        <span className="text-white/72">{label}</span>
        {isLoading ? (
          <BillingSummarySpinner className="h-4 w-4 text-white/90" />
        ) : (
          <span className={`font-semibold ${isOverIncludedQuota ? "text-[#ffb4ab]" : "text-white"}`}>{fmt(value)} / {fmt(max)}</span>
        )}
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-white/12">
        <div
          className={`h-full rounded-full ${isLoading ? "w-1/4 animate-pulse bg-white/35" : barClassName}`}
          style={isLoading ? undefined : { width: `${Math.max(8, Math.min(progress, 100))}%` }}
        />
      </div>
    </div>
  );
}

function getProductPlanDraft(plan: ProductPlan): ProductPlanDraft {
  const parsedSla = parseProductPlanSla(plan.sla);

  return {
    name: plan.name,
    category: plan.category,
    status: plan.status,
    description: plan.description,
    price: { ...plan.price },
    slaEnabled: parsedSla.enabled,
    slaValue: parsedSla.value,
    slaUnit: parsedSla.unit,
    relatedItemIds: [...plan.relatedItemIds],
  };
}

function getProductPlanFromDraft(draft: ProductPlanDraft, id: string): ProductPlan {
  const normalizedSla = draft.slaEnabled ? formatProductPlanSla(draft.slaValue, draft.slaUnit) : null;

  return {
    id,
    name: draft.name.trim(),
    category: draft.category,
    status: draft.status,
    description: draft.description.trim(),
    price: {
      amount: sanitizePriceAmount(draft.price.amount.trim()),
      interval: draft.price.interval,
    },
    sla: normalizedSla,
    relatedItemIds: [...new Set(draft.relatedItemIds)],
  };
}

type BillingUsage = {
  ticketCount: number;
  articleCount: number;
  storageBytes: number;
  storageObjectCount: number;
};

type BillingSummary = {
  tenantSlug: string;
  billingPlan?: string | null;
  billingEmail?: string | null;
  billingName?: string | null;
  customerId?: string | null;
  subscriptionId?: string | null;
  status?: string | null;
  renewalDate?: string | null;
  freeDowngradeExcessDeleteAt?: string | null;
  paymentMethodSummary?: string | null;
  planLimits?: PlanLimits | null;
};

type SettingsViewProps = {
  branding: AppBranding;
  colorMode: ColorMode;
  tenantEmailDomain: string | null;
  language: AppLanguage;
  workspaceSettings: WorkspaceSettings;
  isLoadingSettings?: boolean;
  emailRoutingSyncState: "idle" | "syncing" | "success" | "error";
  emailRoutingSyncMessage: string;
  canManageAdminSettings: boolean;
  canManageBilling: boolean;
  canManageItems: boolean;
  productPlans: ProductPlan[];
  ticketCategories: TicketCategory[];
  knowledgeArticles: KnowledgeArticle[];
  tickets: Ticket[];
  billingUsage?: BillingUsage;
  section?: SettingsSection | null;
  onNavigateToSection?: (section: SettingsSection) => void;
  onBackToSettingsOverview?: () => void;
  onCreateTicketCategory: (ticketCategory: TicketCategory) => void;
  onCreateProductPlan: (productPlan: ProductPlan) => void;
  onDeleteProductPlan: (productPlan: ProductPlan) => void;
  onDeleteTicketCategory: (ticketCategory: TicketCategory) => void;
  onLogoUpload: (file: File | null) => void;
  onPrimaryColorChange: (color: string) => void;
  onConsoleNameChange: (consoleName: string) => void;
  onRefreshCaseEmailRouteOutboundStatuses: (routeId?: string) => Promise<void>;
  onLoadRestorePoints: () => Promise<{
    retentionDays: number | null;
    earliestRestoreTimestamp: string | null;
    latestRestoreTimestamp: string | null;
    earliestRestoreDate: string | null;
    latestRestoreDate: string | null;
  }>;
  onRestoreDatabase: (restoreTimestamp: string) => Promise<{
    message: string;
    restoreDate: string;
    restoreTimestamp: string;
    retentionDays: number | null;
  }>;
  onRemoveLogo: () => void;
  onRefreshWorkspaceSettings?: () => Promise<void>;
  onToggleCaseEmailRouteOutbound: (routeId: string, enabled: boolean) => Promise<{
    enabled: boolean;
    verifiedForSending: boolean;
    verificationStatus: CaseEmailRouteOutboundVerificationStatus;
    verificationError: string | null;
    verifiedAt: string | null;
  }>;
  onUpdateWorkspaceSettings: (settings: WorkspaceSettings) => void;
  onUpdateTicketCategory: (ticketCategory: TicketCategory, previousName: string) => void;
  onUpdateProductPlan: (productPlan: ProductPlan) => void;
};

type SettingsGroupCopy = {
  eyebrow: string;
  title: string;
  description: string;
  items: { title: string; detail: string }[];
};

function SettingsSectionCard({
  section,
  copy,
  onSelect,
}: {
  section: SettingsSection;
  copy: SettingsGroupCopy;
  onSelect: (section: SettingsSection) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(section)}
      className="group flex w-full flex-col rounded-[28px] bg-white p-7 text-left shadow-[0_18px_50px_rgba(15,23,42,0.06)] transition-all hover:-translate-y-0.5 hover:shadow-[0_24px_60px_rgba(15,23,42,0.1)]"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.eyebrow}</p>
      <h2 className="mt-3 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.title}</h2>
      <p className="mt-2 max-w-2xl text-sm text-[#667085]">{copy.description}</p>

      <ul className="mt-5 space-y-2">
        {copy.items.map(item => (
          <li
            key={item.title}
            className="flex items-start gap-3 rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3 transition-colors group-hover:border-[var(--brand-primary-ring)] group-hover:bg-[var(--brand-primary-soft)]"
          >
            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[var(--brand-primary)]" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#101828]">{item.title}</p>
              <p className="mt-1 text-sm text-[#667085]">{item.detail}</p>
            </div>
          </li>
        ))}
      </ul>

      <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--brand-primary)]">
        <span>Open</span>
        <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">→</span>
      </span>
    </button>
  );
}

export function SettingsView({
  branding,
  colorMode,
  tenantEmailDomain,
  language,
  workspaceSettings,
  isLoadingSettings = false,
  emailRoutingSyncState,
  emailRoutingSyncMessage,
  canManageAdminSettings,
  canManageBilling,
  canManageItems,
  productPlans,
  ticketCategories,
  knowledgeArticles,
  tickets,
  billingUsage,
  section = null,
  onNavigateToSection,
  onBackToSettingsOverview,
  onCreateTicketCategory,
  onCreateProductPlan,
  onDeleteProductPlan,
  onDeleteTicketCategory,
  onLogoUpload,
  onPrimaryColorChange,
  onConsoleNameChange,
  onRefreshCaseEmailRouteOutboundStatuses,
  onLoadRestorePoints,
  onRestoreDatabase,
  onRemoveLogo,
  onRefreshWorkspaceSettings,
  onToggleCaseEmailRouteOutbound,
  onUpdateWorkspaceSettings,
  onUpdateTicketCategory,
  onUpdateProductPlan,
}: SettingsViewProps) {
  const copy = settingsViewCopy[language];
  const logsCopy = logsViewCopy[language].settings;
  const localizedAccessLogRetentionOptions = useMemo(() => getAccessLogRetentionOptions(language), [language]);
  const localizedArchiveRetentionOptions = useMemo(() => getArchiveRetentionOptions(language, copy.archiving.noRetentionLabel), [copy.archiving.noRetentionLabel, language]);
  const localizedApiKeyExpiryOptions = useMemo(() => getApiKeyExpiryOptions(language, copy.api.noTenantExpiry), [copy.api.noTenantExpiry, language]);
  const brandRgb = hexToRgb(branding.primaryColor);
  const isDarkMode = colorMode === "dark";
  const selectedCurrency = getCurrencyMeta(workspaceSettings.currency);
  const tenantMailboxDomain = buildTenantMailboxDomain(tenantEmailDomain);
  const portalBrandStyle = {
    "--brand-primary": branding.primaryColor,
    "--brand-primary-strong": mixColor(branding.primaryColor, -0.14),
    "--brand-primary-soft": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.1)`,
    "--brand-primary-shadow": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.2)`,
    "--brand-primary-shadow-strong": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.3)`,
  } as CSSProperties;

  const [selectedProductPlanId, setSelectedProductPlanId] = useState<string | null>(productPlans[0]?.id ?? null);
  const [isCreatingProductPlan, setIsCreatingProductPlan] = useState(false);
  const [isProductPlanModalOpen, setIsProductPlanModalOpen] = useState(false);
  const [openLinkedPopoverId, setOpenLinkedPopoverId] = useState<string | null>(null);
  const [productPlanDraft, setProductPlanDraft] = useState<ProductPlanDraft>(() =>
    productPlans[0] ? getProductPlanDraft(productPlans[0]) : getEmptyProductPlanDraft(),
  );
  const [productPlanError, setProductPlanError] = useState("");
  const [productPlanSavedState, setProductPlanSavedState] = useState(copy.productPlans.syncedState);
  const [productPlanSortColumn, setProductPlanSortColumn] = useState<ProductPlanSortColumn>("name");
  const [productPlanSortDirection, setProductPlanSortDirection] = useState<SortDirection>("asc");
  const [selectedTicketCategoryId, setSelectedTicketCategoryId] = useState<string | null>(ticketCategories[0]?.id ?? null);
  const [isCreatingTicketCategory, setIsCreatingTicketCategory] = useState(false);
  const [isTicketCategoryManagerOpen, setIsTicketCategoryManagerOpen] = useState(false);
  const [isTicketCategoryModalOpen, setIsTicketCategoryModalOpen] = useState(false);
  const [ticketCategoryDraft, setTicketCategoryDraft] = useState<TicketCategoryDraft>(() =>
    ticketCategories[0] ? getTicketCategoryDraft(ticketCategories[0]) : getEmptyTicketCategoryDraft(),
  );
  const [ticketCategoryError, setTicketCategoryError] = useState("");
  const [ticketCategorySearch, setTicketCategorySearch] = useState("");
  const [ticketCategorySortColumn, setTicketCategorySortColumn] = useState<TicketCategorySortColumn>("name");
  const [ticketCategorySortDirection, setTicketCategorySortDirection] = useState<SortDirection>("asc");
  const [caseEmailRouteDrafts, setCaseEmailRouteDrafts] = useState<CaseEmailRouteDraft[]>(() => getCaseEmailRouteDrafts(workspaceSettings));
  const [notificationSettingsDraft, setNotificationSettingsDraft] = useState<WorkspaceNotificationSettings>(() => getWorkspaceNotificationDraft(workspaceSettings));
  const [isNotificationSettingsModalOpen, setIsNotificationSettingsModalOpen] = useState(false);
  const [caseEmailRouteError, setCaseEmailRouteError] = useState("");
  const [caseEmailRouteSearch, setCaseEmailRouteSearch] = useState("");
  const caseEmailRouteListRef = useRef<HTMLDivElement | null>(null);
  const [isMailboxManagerOpen, setIsMailboxManagerOpen] = useState(false);
  const [caseEmailRouteOutboundBusyId, setCaseEmailRouteOutboundBusyId] = useState<string | null>(null);
  const [caseEmailRouteRefreshBusyId, setCaseEmailRouteRefreshBusyId] = useState<string | null>(null);
  const [caseEmailRouteFeedbackId, setCaseEmailRouteFeedbackId] = useState<string | null>(null);
  const [caseEmailRouteOutboundMessage, setCaseEmailRouteOutboundMessage] = useState("");
  const [caseEmailRouteOutboundError, setCaseEmailRouteOutboundError] = useState("");
  const [activeCaseEmailRouteTemplateId, setActiveCaseEmailRouteTemplateId] = useState<string | null>(null);
  const [caseEmailTemplateEditorDraft, setCaseEmailTemplateEditorDraft] = useState<CaseEmailTemplateEditorDraft | null>(null);
  const [isRestoreDatabaseModalOpen, setIsRestoreDatabaseModalOpen] = useState(false);
  const [selectedRestoreTimestampInput, setSelectedRestoreTimestampInput] = useState("");
  const itemNameById = useMemo(
    () => new Map(productPlans.map(plan => [plan.id, plan.name] as const)),
    [productPlans],
  );
  const [isRestorePointsLoading, setIsRestorePointsLoading] = useState(false);
  const [restorePointsError, setRestorePointsError] = useState("");
  const [loadedRestoreRetentionDays, setLoadedRestoreRetentionDays] = useState<number | null>(null);
  const [restoreWindowSummary, setRestoreWindowSummary] = useState<{
    earliestTimestamp: string | null;
    latestTimestamp: string | null;
  }>({
    earliestTimestamp: null,
    latestTimestamp: null,
  });
  const [isRestoringDatabase, setIsRestoringDatabase] = useState(false);
  const [restoreDatabaseError, setRestoreDatabaseError] = useState("");
  const [restoreDatabaseSuccess, setRestoreDatabaseSuccess] = useState("");
  const [isOpeningBillingPortal, setIsOpeningBillingPortal] = useState(false);
  const [isOpeningBillingCheckoutPlan, setIsOpeningBillingCheckoutPlan] = useState<string | null>(null);
  const [isBillingUpgradeModalOpen, setIsBillingUpgradeModalOpen] = useState(false);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [isBillingSummaryLoading, setIsBillingSummaryLoading] = useState(false);
  const [primaryColorDraft, setPrimaryColorDraft] = useState(branding.primaryColor);
  const [consoleNameDraft, setConsoleNameDraft] = useState(branding.consoleName ?? "");
  const [billingPortalError, setBillingPortalError] = useState("");
  const hasRefreshedOutboundStatusesRef = useRef(false);
  const billingSummaryRequestIdRef = useRef(0);

  const emailRoutingSyncBadgeClassName = emailRoutingSyncState === "success"
    ? "bg-[#ecfdf3] text-[#027a48]"
    : emailRoutingSyncState === "error"
      ? "bg-[#fef3f2] text-[#b42318]"
      : emailRoutingSyncState === "syncing"
        ? "bg-[#eff8ff] text-[#175cd3]"
        : "bg-[#f3f4f6] text-[#475467]";

  const emailRoutingSyncBadgeLabel = emailRoutingSyncState === "success"
    ? copy.emailIntake.syncBadge.success
    : emailRoutingSyncState === "error"
      ? copy.emailIntake.syncBadge.error
      : emailRoutingSyncState === "syncing"
        ? copy.emailIntake.syncBadge.syncing
        : copy.emailIntake.syncBadge.idle;

  const localizedEmailRoutingSyncMessage = localizeEmailRoutingSyncMessage(emailRoutingSyncMessage, language, copy.emailIntake.syncMessages);
  const localizedTicketCategorySortHeaders: Array<{ key: TicketCategorySortColumn; label: string }> = [
    { key: "name", label: copy.categories.table.category },
    { key: "usage", label: copy.categories.table.usage },
    { key: "status", label: copy.categories.table.status },
  ];

  const selectableRelatedItems = productPlans.filter(plan => plan.id !== selectedProductPlanId);
  const getTicketCategoryTicketUsage = (categoryName: string) => tickets.filter(ticket => ticket.category === categoryName).length;
  const getTicketCategoryArticleUsage = (categoryName: string) => knowledgeArticles.filter(article => article.audience === categoryName).length;
  const getTicketCategoryUsage = (categoryName: string) => getTicketCategoryTicketUsage(categoryName) + getTicketCategoryArticleUsage(categoryName);
  const getRelatedItemNames = (itemIds: string[]) =>
    itemIds
      .map(itemId => productPlans.find(plan => plan.id === itemId)?.name)
      .filter((name): name is string => Boolean(name));
  const effectiveBillingPlan = billingSummary?.billingPlan?.trim() || workspaceSettings.billingPlan;
  const normalizedEffectiveBillingPlan = effectiveBillingPlan.trim().toLowerCase();
  const hasPaidBillingPlan = normalizedEffectiveBillingPlan === "solo" || normalizedEffectiveBillingPlan === "growth";
  const restoreDisplayTimezone = normalizeWorkspaceTimezone(workspaceSettings.timezone) ?? "UTC";
  const normalizedSelectedRestoreTimestamp = useMemo(
    () => normalizeDateTimeForStorage(selectedRestoreTimestampInput, restoreDisplayTimezone),
    [selectedRestoreTimestampInput, restoreDisplayTimezone],
  );
  const formattedRestoreWindowStart = useMemo(
    () => restoreWindowSummary.earliestTimestamp
      ? formatRestorePointTimestamp(restoreWindowSummary.earliestTimestamp, language, restoreDisplayTimezone)
      : null,
    [language, restoreDisplayTimezone, restoreWindowSummary.earliestTimestamp],
  );
  const formattedRestoreWindowEnd = useMemo(
    () => restoreWindowSummary.latestTimestamp
      ? formatRestorePointTimestamp(restoreWindowSummary.latestTimestamp, language, restoreDisplayTimezone)
      : null,
    [language, restoreDisplayTimezone, restoreWindowSummary.latestTimestamp],
  );
  const restoreWindowMinInput = useMemo(
    () => restoreWindowSummary.earliestTimestamp
      ? formatDateTimeForInput(restoreWindowSummary.earliestTimestamp, restoreDisplayTimezone, true)
      : "",
    [restoreDisplayTimezone, restoreWindowSummary.earliestTimestamp],
  );
  const restoreWindowMaxInput = useMemo(
    () => restoreWindowSummary.latestTimestamp
      ? formatDateTimeForInput(restoreWindowSummary.latestTimestamp, restoreDisplayTimezone, true)
      : "",
    [restoreDisplayTimezone, restoreWindowSummary.latestTimestamp],
  );
  const planDisplayName =
    normalizedEffectiveBillingPlan === "free" ? "Free"
    : normalizedEffectiveBillingPlan === "internal" ? "Internal"
    : normalizedEffectiveBillingPlan === "solo" ? "Solo"
    : normalizedEffectiveBillingPlan === "growth" ? "Growth"
    : effectiveBillingPlan;

  const ticketCount = billingUsage?.ticketCount ?? tickets.length;
  const articleCount = billingUsage?.articleCount ?? knowledgeArticles.length;
  const logCount = billingUsage?.storageObjectCount ?? 0;

  const planLimits = billingSummary?.planLimits ?? workspaceSettings.planLimits;
  const restoreRetentionDays = normalizedEffectiveBillingPlan === "free" ? 7 : 30;
  const ticketUsageThreshold = planLimits
    ? getUsageThreshold(planLimits.ticketLimit, ticketCount, planLimits.overageTicketQty)
    : { max: Math.max(ticketCount, 1), isOverIncludedQuota: false };
  const articleUsageThreshold = planLimits
    ? getUsageThreshold(planLimits.articleLimit, articleCount, planLimits.overageArticleQty)
    : { max: Math.max(articleCount, 1), isOverIncludedQuota: false };
  const storageUsageThreshold = planLimits
    ? getUsageThreshold(planLimits.logCountLimit, logCount, planLimits.overageLogQty)
    : { max: Math.max(logCount, 1), isOverIncludedQuota: false };
  const ticketLimit = ticketUsageThreshold.max;
  const articleLimit = articleUsageThreshold.max;
  const storageLimit = storageUsageThreshold.max;
  const ticketUsageProgress = (ticketCount / ticketLimit) * 100;
  const articleUsageProgress = (articleCount / articleLimit) * 100;
  const storageUsageProgress = (logCount / storageLimit) * 100;
  const isLogCountLoading = isBillingSummaryLoading || (isLoadingSettings && !billingUsage);
  const canViewBillingPanel = canManageAdminSettings || canManageBilling || canManageItems;
  const canChangeSubscriptionPlan = canManageBilling && hasPaidBillingPlan && Boolean(billingSummary?.subscriptionId?.trim());
  const shouldShowPlanSelectionOptions = canManageBilling && (normalizedEffectiveBillingPlan === "free" || canChangeSubscriptionPlan);
  const shouldShowBillingPortal = canManageBilling && hasPaidBillingPlan;
  const billingUpgradePlans = [
    {
      slug: "solo" as const,
      badge: null,
      name: copy.billing.upgradeModal.soloName,
      price: copy.billing.upgradeModal.soloPrice,
      description: copy.billing.upgradeModal.soloDescription,
      features: [
        copy.billing.upgradeModal.soloFeatures.tickets,
        copy.billing.upgradeModal.soloFeatures.articles,
        copy.billing.upgradeModal.soloFeatures.storage,
        copy.billing.upgradeModal.soloFeatures.overages,
      ],
    },
    {
      slug: "growth" as const,
      badge: copy.billing.upgradeModal.growthBadge,
      name: copy.billing.upgradeModal.growthName,
      price: copy.billing.upgradeModal.growthPrice,
      description: copy.billing.upgradeModal.growthDescription,
      features: [
        copy.billing.upgradeModal.growthFeatures.tickets,
        copy.billing.upgradeModal.growthFeatures.articles,
        copy.billing.upgradeModal.growthFeatures.storage,
        copy.billing.upgradeModal.growthFeatures.overages,
      ],
    },
  ];

  const openBillingUpgradeModal = () => {
    if (isOpeningBillingCheckoutPlan) {
      return;
    }

    setBillingPortalError("");
    setIsBillingUpgradeModalOpen(true);
  };

  const closeBillingUpgradeModal = () => {
    if (isOpeningBillingCheckoutPlan) {
      return;
    }

    setIsBillingUpgradeModalOpen(false);
  };

  const refreshBillingSummary = useCallback(async () => {
    if (!canViewBillingPanel) {
      return;
    }

    const requestId = billingSummaryRequestIdRef.current + 1;
    billingSummaryRequestIdRef.current = requestId;
    setIsBillingSummaryLoading(true);

    try {
      const response = await fetch("/api/settings/billing/summary", {
        method: "GET",
        headers: {
          accept: "application/json",
        },
      });
      const payload = await response.json().catch(() => null) as (BillingSummary & { error?: string }) | null;

      if (!response.ok) {
        throw new Error(payload?.error ?? copy.billing.manageBillingError);
      }

      if (billingSummaryRequestIdRef.current !== requestId) {
        return;
      }

      setBillingSummary(payload ?? null);

      const nextBillingPlan = typeof payload?.billingPlan === "string" ? payload.billingPlan.trim() : "";

      const nextPlanLimits = payload?.planLimits ?? null;

      if (
        (nextBillingPlan && nextBillingPlan !== workspaceSettings.billingPlan)
        || JSON.stringify(nextPlanLimits) !== JSON.stringify(workspaceSettings.planLimits ?? null)
      ) {
        await onRefreshWorkspaceSettings?.();
      }
    } catch (error) {
      console.warn("Failed to refresh billing summary.", error);
    } finally {
      if (billingSummaryRequestIdRef.current === requestId) {
        setIsBillingSummaryLoading(false);
      }
    }
  }, [canViewBillingPanel, copy.billing.manageBillingError, onRefreshWorkspaceSettings, workspaceSettings.billingPlan]);

  useEffect(() => {
    void refreshBillingSummary();
  }, [refreshBillingSummary]);

  useEffect(() => {
    setPrimaryColorDraft(branding.primaryColor);
  }, [branding.primaryColor]);

  const handleOpenBillingCheckout = async (planSlug: "solo" | "growth") => {
    if (isOpeningBillingCheckoutPlan || isOpeningBillingPortal) {
      return;
    }

    if (normalizedEffectiveBillingPlan === planSlug) {
      return;
    }

    setIsOpeningBillingCheckoutPlan(planSlug);
    setBillingPortalError("");

    const isPaidPlanChange = hasPaidBillingPlan;
    const endpoint = isPaidPlanChange ? "/api/settings/billing/change-plan" : "/api/settings/billing/checkout";
    const fallbackErrorMessage = isPaidPlanChange ? copy.billing.changePlanError : copy.billing.openCheckoutError;

    // Open the checkout tab synchronously within the click gesture so browsers
    // don't block it as a popup after the async request completes.
    const checkoutWindow = isPaidPlanChange ? null : window.open("", "_blank");

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        body: JSON.stringify({ planSlug }),
      });
      const payload = await response.json().catch(() => null) as {
        error?: string;
        url?: string;
        clientToken?: string | null;
        environment?: "sandbox" | "production" | null;
        transactionId?: string | null;
        changed?: boolean;
      } | null;

      if (!response.ok) {
        throw new Error(payload?.error ?? fallbackErrorMessage);
      }

      if (isPaidPlanChange) {
        setIsBillingUpgradeModalOpen(false);
        await refreshBillingSummary();
        return;
      }

      const checkoutUrl = typeof payload?.url === "string" ? payload.url.trim() : "";

      if (!checkoutUrl) {
        throw new Error(copy.billing.openCheckoutError);
      }

      setIsBillingUpgradeModalOpen(false);

      if (checkoutWindow && !checkoutWindow.closed) {
        checkoutWindow.location.href = checkoutUrl;
      } else {
        window.open(checkoutUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      checkoutWindow?.close();
      const message = error instanceof Error ? error.message : fallbackErrorMessage;
      setBillingPortalError(message);
      window.alert(message);
    } finally {
      setIsOpeningBillingCheckoutPlan(null);
    }
  };

  const handleOpenBillingPortal = async () => {
    if (isOpeningBillingPortal) {
      return;
    }

    setIsOpeningBillingPortal(true);
    setBillingPortalError("");

    try {
      const response = await fetch("/api/settings/billing/manage", {
        method: "GET",
        headers: {
          accept: "application/json",
        },
      });
      const payload = await response.json().catch(() => null) as { error?: string; url?: string } | null;

      if (!response.ok) {
        throw new Error(payload?.error ?? copy.billing.manageBillingError);
      }

      const portalUrl = typeof payload?.url === "string" ? payload.url.trim() : "";

      if (!portalUrl) {
        throw new Error(copy.billing.manageBillingError);
      }

      void refreshBillingSummary();
      window.open(portalUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      const message = error instanceof Error ? error.message : copy.billing.manageBillingError;
      setBillingPortalError(message);
      window.alert(message);
    } finally {
      setIsOpeningBillingPortal(false);
    }
  };

  const billingRenewalValue = (() => {
    const value = billingSummary?.renewalDate?.trim();

    if (!value) {
      return copy.billing.notAvailable;
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(language === "ja" ? "ja-JP" : "en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(parsed);
  })();

  const billingFreeDowngradeDeletionValue = (() => {
    const value = billingSummary?.freeDowngradeExcessDeleteAt?.trim();

    if (!value) {
      return null;
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(language === "ja" ? "ja-JP" : "en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(parsed);
  })();

  const billingContactValue = billingSummary?.billingEmail?.trim()
    || billingSummary?.billingName?.trim()
    || workspaceSettings.billingContact?.trim()
    || copy.billing.notAvailable;
  const billingSupportEmail = "accounts@kuii.net";
  const [billingSupportNoticeBeforeEmail, billingSupportNoticeAfterEmail] = copy.billing.billingSupportNotice.split(billingSupportEmail);
  const shouldShowFreePlanDowngradeWarning = Boolean(
    !isBillingSummaryLoading
    && normalizedEffectiveBillingPlan === "free"
    && billingFreeDowngradeDeletionValue,
  );

  const sortedProductPlans = useMemo(() => {
    const directionMultiplier = productPlanSortDirection === "asc" ? 1 : -1;

    return [...productPlans].sort((left, right) => {
      let comparison = 0;

      switch (productPlanSortColumn) {
        case "name":
          comparison = compareText(left.name, right.name);
          break;
        case "status":
          comparison = productPlanStatuses.indexOf(left.status) - productPlanStatuses.indexOf(right.status);
          break;
        case "linked":
          comparison = left.relatedItemIds.length - right.relatedItemIds.length;
          break;
        case "category":
          comparison = compareText(left.category, right.category);
          break;
        case "price":
          comparison = Number(left.price.amount || 0) - Number(right.price.amount || 0);
          break;
      }

      if (comparison === 0) {
        comparison = compareText(left.name, right.name);
      }

      return comparison * directionMultiplier;
    });
  }, [productPlanSortColumn, productPlanSortDirection, productPlans]);

  const sortedTicketCategories = useMemo(() => {
    const directionMultiplier = ticketCategorySortDirection === "asc" ? 1 : -1;

    return [...ticketCategories].sort((left, right) => {
      const leftUsage = getTicketCategoryUsage(left.name);
      const rightUsage = getTicketCategoryUsage(right.name);
      let comparison = 0;

      switch (ticketCategorySortColumn) {
        case "name":
          comparison = compareText(left.name, right.name);
          break;
        case "usage":
          comparison = leftUsage - rightUsage;
          break;
        case "status":
          comparison = Number(leftUsage > 0) - Number(rightUsage > 0);
          break;
      }

      if (comparison === 0) {
        comparison = compareText(left.name, right.name);
      }

      return comparison * directionMultiplier;
    });
  }, [knowledgeArticles, ticketCategories, ticketCategorySortColumn, ticketCategorySortDirection, tickets]);

  const filteredTicketCategories = useMemo(() => {
    const normalizedSearch = ticketCategorySearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return sortedTicketCategories;
    }

    return sortedTicketCategories.filter(category => category.name.trim().toLowerCase().includes(normalizedSearch));
  }, [sortedTicketCategories, ticketCategorySearch]);

  const filteredCaseEmailRouteDrafts = useMemo(() => {
    const normalizedSearch = caseEmailRouteSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return caseEmailRouteDrafts;
    }

    return caseEmailRouteDrafts.filter(route => {
      const mailboxName = route.mailbox.trim().toLowerCase();
      const managedAddress = (buildManagedSupportAddress(route.mailbox, tenantMailboxDomain) ?? "").toLowerCase();

      return mailboxName.includes(normalizedSearch) || managedAddress.includes(normalizedSearch);
    });
  }, [caseEmailRouteDrafts, caseEmailRouteSearch, tenantMailboxDomain]);

  const categoryUsageSummary = useMemo(() => {
    const inUseCount = ticketCategories.filter(category => getTicketCategoryUsage(category.name) > 0).length;

    return {
      inUseCount,
      readyCount: Math.max(ticketCategories.length - inUseCount, 0),
      preview: sortedTicketCategories.slice(0, 4),
    };
  }, [knowledgeArticles, sortedTicketCategories, ticketCategories, tickets]);

  const mailboxSummaryPreview = useMemo(() => caseEmailRouteDrafts.slice(0, 4), [caseEmailRouteDrafts]);
  const normalizedNotificationSettingsDraft = useMemo(
    () => normalizeWorkspaceNotificationSettingsDraft(notificationSettingsDraft),
    [notificationSettingsDraft],
  );
  const persistedNotificationSettings = useMemo(
    () => getWorkspaceNotificationDraft(workspaceSettings),
    [workspaceSettings],
  );
  const enabledNotificationCount = countEnabledWorkspaceNotificationRules(normalizedNotificationSettingsDraft);
  const hasPendingNotificationChanges = JSON.stringify(normalizedNotificationSettingsDraft) !== JSON.stringify(persistedNotificationSettings);
  const notificationEventOrder: WorkspaceNotificationEventKey[] = ["ticketCreated", "ticketUpdated", "ticketScheduledReminder", "ticketSlaWarning", "ticketSlaBreached"];
  const notificationSummaryPreview = notificationEventOrder.slice(0, 4);
  const workspaceTimezoneChoices = useMemo(
    () => getWorkspaceTimezoneOptions(workspaceSettings.timezone),
    [workspaceSettings.timezone],
  );

  const handleProductPlanSort = (column: ProductPlanSortColumn) => {
    if (productPlanSortColumn === column) {
      setProductPlanSortDirection(current => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setProductPlanSortColumn(column);
    setProductPlanSortDirection(column === "price" || column === "linked" ? "desc" : "asc");
  };

  const loadRestorePoints = async () => {
    setIsRestorePointsLoading(true);
    setRestorePointsError("");

    try {
      const payload = await onLoadRestorePoints();
      const earliestRestoreInput = payload.earliestRestoreTimestamp
        ? formatDateTimeForInput(payload.earliestRestoreTimestamp, restoreDisplayTimezone, true)
        : "";
      const latestRestoreInput = payload.latestRestoreTimestamp
        ? formatDateTimeForInput(payload.latestRestoreTimestamp, restoreDisplayTimezone, true)
        : "";

      setLoadedRestoreRetentionDays(payload.retentionDays);
      setRestoreWindowSummary({
        earliestTimestamp: payload.earliestRestoreTimestamp,
        latestTimestamp: payload.latestRestoreTimestamp,
      });
      setSelectedRestoreTimestampInput(current => {
        if (
          current
          && (!earliestRestoreInput || current >= earliestRestoreInput)
          && (!latestRestoreInput || current <= latestRestoreInput)
        ) {
          return current;
        }

        return latestRestoreInput;
      });
    } catch (error) {
      setSelectedRestoreTimestampInput("");
      setRestoreWindowSummary({ earliestTimestamp: null, latestTimestamp: null });
      setRestorePointsError(error instanceof Error ? error.message : copy.databaseRestore.loadError);
    } finally {
      setIsRestorePointsLoading(false);
    }
  };

  const handleRestoreDatabase = async () => {
    if (!selectedRestoreTimestampInput || isRestoringDatabase) {
      return;
    }

    if (!normalizedSelectedRestoreTimestamp) {
      setRestoreDatabaseError(copy.databaseRestore.invalidSelection);
      return;
    }

    const confirmed = window.confirm(
      copy.databaseRestore.confirmation(
        formatRestorePointTimestamp(normalizedSelectedRestoreTimestamp, language, restoreDisplayTimezone),
      ),
    );

    if (!confirmed) {
      return;
    }

    setIsRestoringDatabase(true);
    setRestoreDatabaseError("");
    setRestoreDatabaseSuccess("");

    try {
      const result = await onRestoreDatabase(normalizedSelectedRestoreTimestamp);
      setRestoreDatabaseSuccess(copy.databaseRestore.success(formatRestorePointTimestamp(result.restoreTimestamp, language, restoreDisplayTimezone)));
      window.setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setRestoreDatabaseError(error instanceof Error ? error.message : copy.databaseRestore.error);
    } finally {
      setIsRestoringDatabase(false);
    }
  };

  const openRestoreDatabaseModal = () => {
    setRestoreDatabaseError("");
    setRestoreDatabaseSuccess("");
    setIsRestoreDatabaseModalOpen(true);
  };

  const closeRestoreDatabaseModal = () => {
    if (isRestoringDatabase) {
      return;
    }

    setIsRestoreDatabaseModalOpen(false);
  };

  const handleTicketCategorySort = (column: TicketCategorySortColumn) => {
    if (ticketCategorySortColumn === column) {
      setTicketCategorySortDirection(current => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setTicketCategorySortColumn(column);
    setTicketCategorySortDirection(column === "usage" || column === "status" ? "desc" : "asc");
  };

  useEffect(() => {
    if (isCreatingProductPlan) {
      return;
    }

    const selectedPlan = productPlans.find(plan => plan.id === selectedProductPlanId) ?? productPlans[0];

    if (!selectedPlan) {
      setSelectedProductPlanId(null);
      setProductPlanDraft(getEmptyProductPlanDraft());
      return;
    }

    if (selectedPlan.id !== selectedProductPlanId) {
      setSelectedProductPlanId(selectedPlan.id);
    }

    setProductPlanDraft(getProductPlanDraft(selectedPlan));
  }, [isCreatingProductPlan, productPlans, selectedProductPlanId]);

  useEffect(() => {
    if (isCreatingTicketCategory) {
      return;
    }

    const selectedCategory = ticketCategories.find(category => category.id === selectedTicketCategoryId) ?? ticketCategories[0];

    if (!selectedCategory) {
      setSelectedTicketCategoryId(null);
      setTicketCategoryDraft(getEmptyTicketCategoryDraft());
      return;
    }

    if (selectedCategory.id !== selectedTicketCategoryId) {
      setSelectedTicketCategoryId(selectedCategory.id);
    }

    setTicketCategoryDraft(getTicketCategoryDraft(selectedCategory));
  }, [isCreatingTicketCategory, selectedTicketCategoryId, ticketCategories]);

  useEffect(() => {
    setCaseEmailRouteDrafts(getCaseEmailRouteDrafts(workspaceSettings));
  }, [workspaceSettings]);

  useEffect(() => {
    setNotificationSettingsDraft(getWorkspaceNotificationDraft(workspaceSettings));
  }, [workspaceSettings]);

  useEffect(() => {
    setCaseEmailRouteOutboundError("");
    setCaseEmailRouteFeedbackId(null);
  }, [workspaceSettings.caseEmailRoutes]);

  useEffect(() => {
    if (hasRefreshedOutboundStatusesRef.current || !canManageAdminSettings || !workspaceSettings.caseEmailRoutes.some(route => route.outboundEnabled)) {
      return;
    }

    hasRefreshedOutboundStatusesRef.current = true;
    void onRefreshCaseEmailRouteOutboundStatuses().catch(error => {
      console.warn("Failed to refresh outbound email verification status.", error);
    });
  }, [canManageAdminSettings, onRefreshCaseEmailRouteOutboundStatuses, workspaceSettings.caseEmailRoutes]);

  useEffect(() => {
    if (!isRestoreDatabaseModalOpen) {
      return;
    }

    void loadRestorePoints();
  }, [isRestoreDatabaseModalOpen]);

  const handleProductPlanDraftChange = <K extends keyof ProductPlanDraft>(field: K, value: ProductPlanDraft[K]) => {
    setProductPlanDraft(current => ({ ...current, [field]: value }));
  };

  const handleToggleRelatedItem = (itemId: string) => {
    setProductPlanDraft(current => ({
      ...current,
      relatedItemIds: current.relatedItemIds.includes(itemId)
        ? current.relatedItemIds.filter(id => id !== itemId)
        : [...current.relatedItemIds, itemId],
    }));
  };

  const handleEditProductPlan = (plan: ProductPlan) => {
    setIsCreatingProductPlan(false);
    setSelectedProductPlanId(plan.id);
    setProductPlanDraft(getProductPlanDraft(plan));
    setProductPlanError("");
    setIsProductPlanModalOpen(true);
  };

  const handleCreateProductPlan = () => {
    setIsCreatingProductPlan(true);
    setSelectedProductPlanId(null);
    setProductPlanDraft(getEmptyProductPlanDraft());
    setProductPlanError("");
    setIsProductPlanModalOpen(true);
  };

  const handleEditTicketCategory = (category: TicketCategory) => {
    setIsCreatingTicketCategory(false);
    setSelectedTicketCategoryId(category.id);
    setTicketCategoryDraft(getTicketCategoryDraft(category));
    setTicketCategoryError("");
    setIsTicketCategoryModalOpen(true);
  };

  const handleCreateTicketCategory = () => {
    setIsCreatingTicketCategory(true);
    setSelectedTicketCategoryId(null);
    setTicketCategoryDraft(getEmptyTicketCategoryDraft());
    setTicketCategoryError("");
    setIsTicketCategoryModalOpen(true);
  };

  const handleCloseProductPlanModal = () => {
    setIsProductPlanModalOpen(false);
    setProductPlanError("");

    if (isCreatingProductPlan) {
      setIsCreatingProductPlan(false);

      const fallbackPlan = productPlans.find(plan => plan.id === selectedProductPlanId) ?? productPlans[0];

      if (fallbackPlan) {
        setSelectedProductPlanId(fallbackPlan.id);
        setProductPlanDraft(getProductPlanDraft(fallbackPlan));
      } else {
        setProductPlanDraft(getEmptyProductPlanDraft());
      }

      return;
    }

    const selectedPlan = productPlans.find(plan => plan.id === selectedProductPlanId);

    if (selectedPlan) {
      setProductPlanDraft(getProductPlanDraft(selectedPlan));
    }
  };

  const handleCloseTicketCategoryModal = () => {
    setIsTicketCategoryModalOpen(false);
    setTicketCategoryError("");

    if (isCreatingTicketCategory) {
      setIsCreatingTicketCategory(false);

      const fallbackCategory = ticketCategories.find(category => category.id === selectedTicketCategoryId) ?? ticketCategories[0];

      if (fallbackCategory) {
        setSelectedTicketCategoryId(fallbackCategory.id);
        setTicketCategoryDraft(getTicketCategoryDraft(fallbackCategory));
      } else {
        setTicketCategoryDraft(getEmptyTicketCategoryDraft());
      }

      return;
    }

    const selectedCategory = ticketCategories.find(category => category.id === selectedTicketCategoryId);

    if (selectedCategory) {
      setTicketCategoryDraft(getTicketCategoryDraft(selectedCategory));
    }
  };

  const handleResetProductPlanDraft = () => {
    if (isCreatingProductPlan) {
      setProductPlanDraft(getEmptyProductPlanDraft());
      setProductPlanError("");
      return;
    }

    const selectedPlan = productPlans.find(plan => plan.id === selectedProductPlanId);
    if (!selectedPlan) {
      return;
    }

    setProductPlanDraft(getProductPlanDraft(selectedPlan));
    setProductPlanError("");
  };

  const handleResetTicketCategoryDraft = () => {
    if (isCreatingTicketCategory) {
      setTicketCategoryDraft(getEmptyTicketCategoryDraft());
      setTicketCategoryError("");
      return;
    }

    const selectedCategory = ticketCategories.find(category => category.id === selectedTicketCategoryId);
    if (!selectedCategory) {
      return;
    }

    setTicketCategoryDraft(getTicketCategoryDraft(selectedCategory));
    setTicketCategoryError("");
  };

  const handleTicketCategoryFieldChange = <K extends keyof TicketCategoryCustomField>(fieldId: string, key: K, value: TicketCategoryCustomField[K]) => {
    setTicketCategoryDraft(current => ({
      ...current,
      customFields: current.customFields.map(field => (field.id === fieldId ? { ...field, [key]: value } : field)),
    }));
    setTicketCategoryError("");
  };

  const handleAddTicketCategoryField = (type: TicketCategoryCustomFieldType = "text") => {
    setTicketCategoryDraft(current => ({
      ...current,
      customFields: [...current.customFields, getEmptyTicketCategoryFieldDraft(type)],
    }));
    setTicketCategoryError("");
  };

  const handleDeleteTicketCategoryField = (fieldId: string) => {
    setTicketCategoryDraft(current => ({
      ...current,
      customFields: current.customFields.filter(field => field.id !== fieldId),
    }));
    setTicketCategoryError("");
  };

  const effectiveCaseEmailRouteDrafts = caseEmailTemplateEditorDraft
    ? caseEmailRouteDrafts.map(route => (
        route.id === caseEmailTemplateEditorDraft.routeId
          ? {
              ...route,
              templateSubject: caseEmailTemplateEditorDraft.templateSubject,
              templateHtml: caseEmailTemplateEditorDraft.templateHtml,
            }
          : route
      ))
    : caseEmailRouteDrafts;
  const persistedCaseEmailRouteDrafts = getCaseEmailRouteDrafts(workspaceSettings);
  const isCaseEmailRouteDirty = (routeId: string) => {
    const draft = effectiveCaseEmailRouteDrafts.find(route => route.id === routeId);
    if (!draft) {
      return false;
    }
    const persisted = persistedCaseEmailRouteDrafts.find(route => route.id === routeId);
    if (!persisted) {
      return true;
    }
    return JSON.stringify(draft) !== JSON.stringify(persisted);
  };
  const activeCaseEmailRouteTemplateDraft = activeCaseEmailRouteTemplateId
    ? effectiveCaseEmailRouteDrafts.find(route => route.id === activeCaseEmailRouteTemplateId) ?? null
    : null;
  const activeCaseEmailTemplateEditor = caseEmailTemplateEditorDraft && activeCaseEmailRouteTemplateId === caseEmailTemplateEditorDraft.routeId
    ? caseEmailTemplateEditorDraft
    : null;
  const activeCaseEmailRoutePreviewSubject = activeCaseEmailRouteTemplateDraft
    ? renderEmailTemplate(
        normalizeOutboundTemplateSubject(activeCaseEmailTemplateEditor?.templateSubject ?? activeCaseEmailRouteTemplateDraft.templateSubject) || defaultTicketUpdateEmailTemplateSubject,
        {
          actor_name: sampleEmailTemplatePreview.actor_name,
          recipient_name: sampleEmailTemplatePreview.recipient_name,
          reply_to_address: buildManagedSupportAddress(activeCaseEmailRouteTemplateDraft.mailbox, tenantMailboxDomain) || sampleEmailTemplatePreview.reply_to_address,
          ticket_notes: "Sample note",
          ticket_number: sampleEmailTemplatePreview.ticket_number,
          ticket_status: sampleEmailTemplatePreview.ticket_status,
          ticket_title: sampleEmailTemplatePreview.ticket_title,
          workspace_name: sampleEmailTemplatePreview.workspace_name,
        },
      )
    : "";
  const activeCaseEmailRoutePreviewHtml = activeCaseEmailRouteTemplateDraft
    ? renderEmailTemplate(
        normalizeOutboundTemplateHtml(activeCaseEmailTemplateEditor?.templateHtml ?? activeCaseEmailRouteTemplateDraft.templateHtml) || defaultTicketUpdateEmailTemplateHtml,
        {
          ...sampleEmailTemplatePreview,
          reply_to_address: buildManagedSupportAddress(activeCaseEmailRouteTemplateDraft.mailbox, tenantMailboxDomain) || sampleEmailTemplatePreview.reply_to_address,
        },
      )
    : "";

  const handleCaseEmailRouteDraftChange = (routeId: string, field: keyof CaseEmailRouteDraft, value: string) => {
    setCaseEmailRouteDrafts(currentRoutes => currentRoutes.map(route => (route.id === routeId ? { ...route, [field]: value } : route)));
  };

  const handleToggleWorkspaceNotificationRule = (eventKey: WorkspaceNotificationEventKey) => {
    setNotificationSettingsDraft(current => ({
      ...current,
      [eventKey]: {
        ...current[eventKey],
        enabled: !current[eventKey].enabled,
      },
    }));
  };

  const handleToggleWorkspaceNotificationCategory = (eventKey: WorkspaceNotificationEventKey, categoryId: string) => {
    setNotificationSettingsDraft(current => {
      const currentRule = current[eventKey];
      const hasCategory = currentRule.categoryIds.includes(categoryId);

      return {
        ...current,
        [eventKey]: {
          ...currentRule,
          categoryIds: hasCategory
            ? currentRule.categoryIds.filter(id => id !== categoryId)
            : [...currentRule.categoryIds, categoryId].sort((left, right) => left.localeCompare(right)),
        },
      };
    });
  };

  const handleResetWorkspaceNotifications = () => {
    setNotificationSettingsDraft(getWorkspaceNotificationDraft(workspaceSettings));
  };

  const handleSaveWorkspaceNotifications = (closeModal = false) => {
    onUpdateWorkspaceSettings({
      ...workspaceSettings,
      notifications: normalizedNotificationSettingsDraft,
    });

    if (closeModal) {
      setIsNotificationSettingsModalOpen(false);
    }
  };

  const handleCaseEmailTemplateDraftChange = (
    routeId: string,
    updates: Partial<Pick<CaseEmailRouteDraft, "templateSubject" | "templateHtml">>,
  ) => {
    setCaseEmailTemplateEditorDraft(current => (
      current && current.routeId === routeId
        ? { ...current, ...updates }
        : current
    ));
    setCaseEmailRouteDrafts(currentRoutes => currentRoutes.map(route => (
      route.id === routeId
        ? { ...route, ...updates }
        : route
    )));
  };

  const handleAddCaseEmailRoute = () => {
    setCaseEmailRouteDrafts(currentRoutes => [
      {
        id: createRecordId("case-email-route"),
        mailbox: "",
        categoryId: "",
        itemId: "",
        outboundFromEmail: "",
        outboundVerificationStatus: "disabled",
        outboundVerificationError: "",
        outboundVerifiedAt: "",
        templateSubject: "",
        templateHtml: "",
      },
      ...currentRoutes,
    ]);
    setCaseEmailRouteSearch("");
    caseEmailRouteListRef.current?.scrollTo({ top: 0 });
    setCaseEmailRouteError("");
  };

  const handleDeleteCaseEmailRoute = (routeId: string) => {
    setCaseEmailRouteDrafts(currentRoutes => currentRoutes.filter(route => route.id !== routeId));
    setCaseEmailRouteError("");

    if (activeCaseEmailRouteTemplateId === routeId) {
      setCaseEmailTemplateEditorDraft(null);
      setActiveCaseEmailRouteTemplateId(null);
    }

    const routeIsPersisted = workspaceSettings.caseEmailRoutes.some(route => route.id === routeId);
    if (routeIsPersisted) {
      onUpdateWorkspaceSettings({
        ...workspaceSettings,
        caseEmailRoutes: workspaceSettings.caseEmailRoutes.filter(route => route.id !== routeId),
      });
    }
  };

  const handleResetCaseEmailRoutes = () => {
    setCaseEmailRouteDrafts(getCaseEmailRouteDrafts(workspaceSettings));
    setCaseEmailTemplateEditorDraft(null);
    setActiveCaseEmailRouteTemplateId(null);
    setCaseEmailRouteError("");
  };

  const handleToggleRouteOutbound = async (routeId: string, enabled: boolean) => {
    if (isCaseEmailRouteDirty(routeId)) {
      setCaseEmailRouteFeedbackId(routeId);
      setCaseEmailRouteOutboundError(copy.emailIntake.saveBeforeOutbound);
      setCaseEmailRouteOutboundMessage("");
      return;
    }

    setCaseEmailRouteFeedbackId(routeId);
    setCaseEmailRouteOutboundBusyId(routeId);
    setCaseEmailRouteOutboundError("");
    setCaseEmailRouteOutboundMessage(copy.emailIntake.outboundBusyMessage);

    try {
      const result = await onToggleCaseEmailRouteOutbound(routeId, enabled);
      setCaseEmailRouteOutboundMessage(
        result.enabled
          ? (result.verificationStatus === "ready" ? copy.emailIntake.outboundReadyMessage : copy.emailIntake.outboundPendingMessage)
          : copy.emailIntake.outboundDisabledMessage,
      );
    } catch (error) {
      setCaseEmailRouteOutboundMessage("");
      setCaseEmailRouteOutboundError(error instanceof Error ? error.message : copy.emailIntake.syncMessages.error);
    } finally {
      setCaseEmailRouteOutboundBusyId(null);
    }
  };

  const handleOpenCaseEmailTemplateModal = (routeId: string) => {
    const routeDraft = caseEmailRouteDrafts.find(route => route.id === routeId) ?? null;

    if (routeDraft) {
      setCaseEmailTemplateEditorDraft({
        routeId,
        templateSubject: routeDraft.templateSubject,
        templateHtml: routeDraft.templateHtml,
      });
    }

    setActiveCaseEmailRouteTemplateId(routeId);
  };

  const handleCloseCaseEmailTemplateModal = () => {
    if (caseEmailTemplateEditorDraft) {
      setCaseEmailRouteDrafts(currentRoutes => currentRoutes.map(route => (
        route.id === caseEmailTemplateEditorDraft.routeId
          ? {
              ...route,
              templateSubject: caseEmailTemplateEditorDraft.templateSubject,
              templateHtml: caseEmailTemplateEditorDraft.templateHtml,
            }
          : route
      )));
    }

    setCaseEmailTemplateEditorDraft(null);
    setActiveCaseEmailRouteTemplateId(null);
  };

  const handleLoadDefaultCaseEmailTemplate = () => {
    if (!activeCaseEmailRouteTemplateId) {
      return;
    }

    handleCaseEmailTemplateDraftChange(activeCaseEmailRouteTemplateId, { templateHtml: defaultTicketUpdateEmailTemplateHtml });
  };

  const handleLoadDefaultCaseEmailSubject = () => {
    if (!activeCaseEmailRouteTemplateId) {
      return;
    }

    handleCaseEmailTemplateDraftChange(activeCaseEmailRouteTemplateId, { templateSubject: defaultTicketUpdateEmailTemplateSubject });
  };

  const handleClearCaseEmailTemplate = () => {
    if (!activeCaseEmailRouteTemplateId) {
      return;
    }

    handleCaseEmailTemplateDraftChange(activeCaseEmailRouteTemplateId, { templateHtml: "" });
  };

  const handleRefreshCaseEmailRouteStatus = async (routeId: string) => {
    setCaseEmailRouteFeedbackId(routeId);
    setCaseEmailRouteRefreshBusyId(routeId);
    setCaseEmailRouteOutboundError("");
    setCaseEmailRouteOutboundMessage(copy.emailIntake.outboundBusyMessage);

    try {
      await onRefreshCaseEmailRouteOutboundStatuses(routeId);
      setCaseEmailRouteOutboundMessage(copy.emailIntake.outboundRefreshedMessage);
    } catch (error) {
      setCaseEmailRouteOutboundMessage("");
      setCaseEmailRouteOutboundError(error instanceof Error ? error.message : copy.emailIntake.syncMessages.error);
    } finally {
      setCaseEmailRouteRefreshBusyId(null);
    }
  };

  const handleSaveCaseEmailRoute = (routeId: string) => {
    if (!tenantMailboxDomain) {
      setCaseEmailRouteError(copy.emailIntake.errors.missingTenantDomain);
      return false;
    }

    const routeDraft = effectiveCaseEmailRouteDrafts.find(route => route.id === routeId);

    if (!routeDraft) {
      return false;
    }

    const mailboxName = routeDraft.mailbox.trim().toLowerCase();

    if (!mailboxName) {
      setCaseEmailRouteError(copy.emailIntake.errors.missingMailbox);
      return false;
    }

    if (!isValidMailboxName(mailboxName)) {
      setCaseEmailRouteError(copy.emailIntake.errors.invalidMailbox);
      return false;
    }

    const duplicateMailbox = workspaceSettings.caseEmailRoutes.some(existingRoute => (
      existingRoute.id !== routeId && getEmailLocalPart(existingRoute.email).trim().toLowerCase() === mailboxName
    ));

    if (duplicateMailbox) {
      setCaseEmailRouteError(copy.emailIntake.errors.duplicateMailbox);
      return false;
    }

    if (routeDraft.outboundFromEmail.trim() && !isValidEmailAddress(routeDraft.outboundFromEmail)) {
      setCaseEmailRouteError(copy.emailIntake.errors.invalidOutboundFrom);
      return false;
    }

    const persistedRoute = workspaceSettings.caseEmailRoutes.find(existingRoute => existingRoute.id === routeId);
    const nextEmail = buildManagedSupportAddress(routeDraft.mailbox, tenantMailboxDomain);
    const nextOutboundFromEmail = normalizeRouteOutboundFromEmail(routeDraft.outboundFromEmail) || null;
    const previousSender = persistedRoute
      ? normalizeRouteOutboundFromEmail(persistedRoute.outboundFromEmail) || persistedRoute.email.trim().toLowerCase()
      : "";
    const nextSender = nextOutboundFromEmail || nextEmail;
    const outboundIdentityChanged = Boolean(persistedRoute) && previousSender !== nextSender;
    const outboundEnabled = outboundIdentityChanged ? false : (persistedRoute?.outboundEnabled ?? false);

    const nextRoute: CaseEmailRoute = {
      id: routeDraft.id,
      email: nextEmail,
      categoryId: routeDraft.categoryId.trim() || null,
      itemId: routeDraft.itemId.trim() || null,
      outboundEnabled,
      outboundFromEmail: nextOutboundFromEmail,
      outboundVerificationStatus: outboundEnabled ? (persistedRoute?.outboundVerificationStatus ?? "pending") : "disabled",
      outboundVerificationError: outboundEnabled ? (persistedRoute?.outboundVerificationError ?? null) : null,
      outboundVerifiedAt: outboundEnabled ? (persistedRoute?.outboundVerifiedAt ?? null) : null,
      outboundTemplateSubject: normalizeOutboundTemplateSubject(routeDraft.templateSubject),
      outboundTemplateHtml: normalizeOutboundTemplateHtml(routeDraft.templateHtml),
    };

    const nextCaseEmailRoutes = persistedRoute
      ? workspaceSettings.caseEmailRoutes.map(existingRoute => (existingRoute.id === routeId ? nextRoute : existingRoute))
      : [nextRoute, ...workspaceSettings.caseEmailRoutes];

    onUpdateWorkspaceSettings({
      ...workspaceSettings,
      caseEmailRoutes: nextCaseEmailRoutes,
    });
    setCaseEmailRouteError("");
    setCaseEmailRouteOutboundError("");
    return true;
  };

  const handleProductPlanPriceChange = <K extends keyof ProductPlanPrice>(field: K, value: ProductPlanPrice[K]) => {
    setProductPlanDraft(current => ({
      ...current,
      price: { ...current.price, [field]: value },
    }));
  };

  useEffect(() => {
    if (!openLinkedPopoverId || typeof document === "undefined") {
      return;
    }

    const handlePointerDown = () => {
      setOpenLinkedPopoverId(null);
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [openLinkedPopoverId]);

  useEffect(() => {
    if (!isProductPlanModalOpen || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCloseProductPlanModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCreatingProductPlan, isProductPlanModalOpen, productPlans, selectedProductPlanId]);

  useEffect(() => {
    if (!isTicketCategoryModalOpen || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCloseTicketCategoryModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCreatingTicketCategory, isTicketCategoryModalOpen, selectedTicketCategoryId, ticketCategories]);

  useEffect(() => {
    if (!isTicketCategoryManagerOpen || typeof document === "undefined") {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsTicketCategoryManagerOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isTicketCategoryManagerOpen]);

  useEffect(() => {
    if (!isMailboxManagerOpen || typeof document === "undefined") {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMailboxManagerOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMailboxManagerOpen]);

  useEffect(() => {
    if (!activeCaseEmailRouteTemplateDraft || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCloseCaseEmailTemplateModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeCaseEmailRouteTemplateDraft]);

  const handleSaveProductPlan = () => {
    if (!productPlanDraft.name.trim()) {
      setProductPlanError(copy.productPlans.errors.missingName);
      return;
    }

    if (!productPlanDraft.price.amount.trim()) {
      setProductPlanError(copy.productPlans.errors.missingPrice);
      return;
    }

    if (productPlanDraft.slaEnabled) {
      const numericSlaValue = Number(productPlanDraft.slaValue.trim());

      if (!Number.isFinite(numericSlaValue) || numericSlaValue <= 0) {
        setProductPlanError(copy.productPlans.errors.invalidPositiveSla);
        return;
      }
    }

    if (productPlanDraft.slaEnabled && !formatProductPlanSla(productPlanDraft.slaValue, productPlanDraft.slaUnit)) {
      setProductPlanError(copy.productPlans.errors.invalidSla);
      return;
    }

    const nextProductPlan = getProductPlanFromDraft(
      productPlanDraft,
      isCreatingProductPlan ? createRecordId("plan") : selectedProductPlanId ?? createRecordId("plan"),
    );

    if (isCreatingProductPlan) {
      onCreateProductPlan(nextProductPlan);
      setIsCreatingProductPlan(false);
      setSelectedProductPlanId(nextProductPlan.id);
      setProductPlanSavedState(copy.productPlans.createdState);
    } else {
      onUpdateProductPlan(nextProductPlan);
      setProductPlanSavedState(copy.productPlans.updatedState(nextProductPlan.name));
    }

    setProductPlanDraft(getProductPlanDraft(nextProductPlan));
    setProductPlanError("");
    setIsProductPlanModalOpen(false);
  };

  const handleSaveTicketCategory = () => {
    if (!ticketCategoryDraft.name.trim()) {
      setTicketCategoryError(copy.categories.errors.missingCategoryName);
      return;
    }

    const duplicateFieldLabel = ticketCategoryDraft.customFields
      .map(field => field.label.trim().toLowerCase())
      .filter(Boolean)
      .find((label, index, labels) => labels.indexOf(label) !== index);

    if (ticketCategoryDraft.customFields.some(field => !field.label.trim())) {
      setTicketCategoryError(copy.categories.errors.missingFieldLabel);
      return;
    }

    if (duplicateFieldLabel) {
      setTicketCategoryError(copy.categories.errors.duplicateFieldLabel);
      return;
    }

    const invalidSelectField = ticketCategoryDraft.customFields.find(field => field.type === "select" && field.options.every(option => !option.trim()));

    if (invalidSelectField) {
      setTicketCategoryError(copy.categories.errors.missingSelectOption(invalidSelectField.label));
      return;
    }

    const duplicateCategory = ticketCategories.find(
      category => category.id !== selectedTicketCategoryId && category.name.trim().toLowerCase() === ticketCategoryDraft.name.trim().toLowerCase(),
    );

    if (duplicateCategory) {
      setTicketCategoryError(copy.categories.errors.duplicateCategory);
      return;
    }

    const previousCategory = ticketCategories.find(category => category.id === selectedTicketCategoryId) ?? null;
    const nextTicketCategory = getTicketCategoryFromDraft(
      ticketCategoryDraft,
      isCreatingTicketCategory ? createRecordId("ticket-category") : selectedTicketCategoryId ?? createRecordId("ticket-category"),
    );

    if (isCreatingTicketCategory) {
      onCreateTicketCategory(nextTicketCategory);
      setIsCreatingTicketCategory(false);
      setSelectedTicketCategoryId(nextTicketCategory.id);
    } else {
      onUpdateTicketCategory(nextTicketCategory, previousCategory?.name ?? nextTicketCategory.name);
    }

    setTicketCategoryDraft(getTicketCategoryDraft(nextTicketCategory));
    setTicketCategoryError("");
    setIsTicketCategoryModalOpen(false);
  };

  return (
    <section className="space-y-8 p-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{section ? copy.page.sectionEyebrow(copy.groups[section].title) : copy.page.eyebrow}</p>
          <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{section ? copy.groups[section].title : copy.page.title}</h1>
          <p className="mt-3 max-w-3xl text-sm text-[#667085]">
            {canManageAdminSettings
              ? copy.page.descriptionManage
              : copy.page.descriptionReadOnly}
          </p>
        </div>

        <div className="rounded-[24px] bg-white px-5 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.page.environment}</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#12b76a]" />
            <span className="text-sm font-semibold text-[#101828]">{copy.page.environmentStatus}</span>
          </div>
        </div>
      </div>

      {section ? (
        <button
          type="button"
          onClick={() => { onBackToSettingsOverview?.(); }}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#475467] transition-colors hover:text-[#101828]"
        >
          <span aria-hidden="true">←</span>
          <span>{copy.page.backToSettings}</span>
        </button>
      ) : null}

      {!section ? (
        <div className="flex flex-col gap-6 md:flex-row md:items-start">
          <div className="flex flex-1 flex-col gap-6">
          <SettingsSectionCard
            section="workspace"
            copy={copy.groups.workspace as SettingsGroupCopy}
            onSelect={(nextSection) => { onNavigateToSection?.(nextSection); }}
          />
          <SettingsSectionCard
            section="ticketing"
            copy={copy.groups.ticketing as SettingsGroupCopy}
            onSelect={(nextSection) => { onNavigateToSection?.(nextSection); }}
          />
          </div>
          <div className="flex flex-1 flex-col gap-6">
          {canViewBillingPanel ? (
          <div className="brand-primary-panel relative overflow-hidden rounded-[28px] p-7 text-white">
            <div className="absolute -right-14 -top-14 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <div className="relative">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/72">{copy.billing.eyebrow}</p>
              <h2 className="mt-4 flex flex-wrap items-center gap-3 font-headline text-3xl font-extrabold tracking-[-0.05em]">
                <span>{copy.billing.title} ·</span>
                {isBillingSummaryLoading ? <BillingSummarySpinner className="h-6 w-6 text-white" /> : <span>{planDisplayName}</span>}
              </h2>
              <p className="mt-3 text-sm leading-6 text-white/80">
                {copy.billing.description}{" "}
                {billingSupportNoticeAfterEmail === undefined ? copy.billing.billingSupportNotice : (
                  <>
                    {billingSupportNoticeBeforeEmail}
                    <strong className="font-semibold text-white">{billingSupportEmail}</strong>
                    {billingSupportNoticeAfterEmail}
                  </>
                )}
              </p>
              {shouldShowFreePlanDowngradeWarning ? (
                <div className="mt-4 rounded-[26px] border border-[#fecdca] bg-[linear-gradient(180deg,#fff5f4_0%,#fee4e2_100%)] p-5 shadow-[0_16px_36px_rgba(217,45,32,0.12)] ring-1 ring-[#fecdca]">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#d92d20] text-[11px] font-bold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)]">
                      !
                    </div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#b42318]">{copy.billing.freeDowngradeWarningEyebrow}</p>
                  </div>
                  <div className="mt-3 space-y-3 text-sm leading-6 text-[#7a271a]">
                    <p className="font-semibold text-[#912018]">{copy.billing.freeDowngradeWarningTitle}</p>
                    <p>{copy.billing.freeDowngradeWarningBody(billingFreeDowngradeDeletionValue ?? copy.billing.notAvailable)}</p>
                    <p>{copy.billing.freeDowngradeWarningDetail}</p>
                  </div>
                </div>
              ) : null}
              <div className="mt-3 space-y-4 rounded-[24px] bg-white/12 p-5 backdrop-blur-sm">
                {[
                  [copy.billing.renewalDate, billingRenewalValue],
                  [copy.billing.billingContact, billingContactValue],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-white/72">{label}</span>
                    {isBillingSummaryLoading ? (
                      <BillingSummarySpinner className="h-4 w-4 text-white/90" />
                    ) : (
                      <span className="font-semibold text-white">{value}</span>
                    )}
                  </div>
                ))}

                <div className="my-1 h-px bg-white/12" />

                <BillingProgressRow
                  label={copy.billing.ticketsTracked}
                  value={ticketCount}
                  max={ticketLimit}
                  progress={ticketUsageProgress}
                  barClassName={ticketUsageThreshold.isOverIncludedQuota
                    ? "bg-[linear-gradient(90deg,#f97066,#f04438)]"
                    : "bg-[linear-gradient(90deg,rgba(255,255,255,0.78),rgba(255,255,255,0.96))]"}
                  formatValue={formatCount}
                  isOverIncludedQuota={ticketUsageThreshold.isOverIncludedQuota}
                  isLoading={isBillingSummaryLoading}
                />
                <BillingProgressRow
                  label={copy.billing.articlesTracked}
                  value={articleCount}
                  max={articleLimit}
                  progress={articleUsageProgress}
                  barClassName={articleUsageThreshold.isOverIncludedQuota
                    ? "bg-[linear-gradient(90deg,#f97066,#f04438)]"
                    : "bg-[linear-gradient(90deg,rgba(255,255,255,0.52),rgba(255,255,255,0.82))]"}
                  formatValue={formatCount}
                  isOverIncludedQuota={articleUsageThreshold.isOverIncludedQuota}
                  isLoading={isBillingSummaryLoading}
                />
                <BillingProgressRow
                  label={copy.billing.storageUsed}
                  value={logCount}
                  max={storageLimit}
                  progress={storageUsageProgress}
                  barClassName={storageUsageThreshold.isOverIncludedQuota
                    ? "bg-[linear-gradient(90deg,#f97066,#f04438)]"
                    : "bg-[linear-gradient(90deg,rgba(255,255,255,0.28),rgba(255,255,255,0.48))]"}
                  formatValue={formatCount}
                  isOverIncludedQuota={storageUsageThreshold.isOverIncludedQuota}
                  isLoading={isLogCountLoading}
                />
              </div>
              {!isBillingSummaryLoading && (shouldShowPlanSelectionOptions || shouldShowBillingPortal) ? (
                <div className="mt-6 flex flex-wrap gap-3">
                  {shouldShowPlanSelectionOptions ? (
                    <button
                      type="button"
                      onClick={openBillingUpgradeModal}
                      disabled={Boolean(isOpeningBillingCheckoutPlan)}
                      className="brand-primary-text rounded-2xl bg-white px-5 py-3 text-sm font-semibold disabled:cursor-wait disabled:opacity-70"
                    >
                      {normalizedEffectiveBillingPlan === "free" ? copy.billing.upgradeAction : copy.billing.changePlanAction}
                    </button>
                  ) : null}
                  {shouldShowBillingPortal ? (
                    <button
                      type="button"
                      onClick={() => {
                        void handleOpenBillingPortal();
                      }}
                      disabled={isOpeningBillingPortal || Boolean(isOpeningBillingCheckoutPlan)}
                      aria-busy={isOpeningBillingPortal}
                      className="rounded-2xl border border-white/35 bg-transparent px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-wait disabled:opacity-70"
                    >
                      {isOpeningBillingPortal ? copy.billing.openingBilling : copy.billing.manageBilling}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {!isBillingSummaryLoading && shouldShowBillingPortal && billingPortalError ? <p className="mt-3 text-sm text-white/88">{billingPortalError}</p> : null}
              {shouldShowPlanSelectionOptions && normalizedEffectiveBillingPlan === "free" && billingPortalError ? <p className="mt-3 text-sm text-white/88">{billingPortalError}</p> : null}
            </div>
          </div>
          ) : null}
          <SettingsSectionCard
            section="security"
            copy={copy.groups.security as SettingsGroupCopy}
            onSelect={(nextSection) => { onNavigateToSection?.(nextSection); }}
          />
          </div>
        </div>
      ) : null}

      <AnimatedModal
        open={shouldShowPlanSelectionOptions && isBillingUpgradeModalOpen}
        onClose={closeBillingUpgradeModal}
        backdropAriaLabel={copy.billing.upgradeModal.closeAria}
        overlayClassName="fixed inset-0 z-[140] bg-[#101828]/35 backdrop-blur-[6px]"
        overlayStyle={portalBrandStyle}
        dialogClassName="app-modal-dialog relative max-h-[calc(100vh-4rem)] w-full max-w-5xl overflow-y-auto rounded-[32px] border border-white/70 bg-[#fcfcfd] p-6 shadow-[0_32px_100px_rgba(15,23,42,0.24)] custom-scrollbar"
        dialogStyle={isDarkMode ? {
          borderColor: "rgba(255, 255, 255, 0.08)",
          backgroundColor: "#232327",
          boxShadow: "0 32px 100px rgba(0, 0, 0, 0.44)",
        } : undefined}
      >
        <button
          type="button"
          onClick={closeBillingUpgradeModal}
          className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
          style={isDarkMode ? {
            backgroundColor: "rgba(39, 39, 42, 0.96)",
            color: "#d4d4d8",
            boxShadow: "0 12px 24px rgba(0, 0, 0, 0.28)",
          } : undefined}
          aria-label={copy.billing.upgradeModal.closeAria}
        >
          <span aria-hidden="true" className="text-lg leading-none">×</span>
        </button>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]" style={isDarkMode ? { color: "#a1a1aa" } : undefined}>{copy.billing.upgradeModal.eyebrow}</p>
          <h3 className="mt-3 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]" style={isDarkMode ? { color: "#fafafa" } : undefined}>{copy.billing.upgradeModal.title}</h3>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-[#667085]" style={isDarkMode ? { color: "#d4d4d8" } : undefined}>{copy.billing.upgradeModal.description}</p>

          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            {billingUpgradePlans.map(plan => {
              const isOpening = isOpeningBillingCheckoutPlan === plan.slug;
              const isCurrentPlan = normalizedEffectiveBillingPlan === plan.slug;
              const isGrowthPlan = plan.slug === "growth";
              const cardStyle = isDarkMode
                ? isGrowthPlan
                  ? {
                      borderColor: "rgba(var(--brand-primary-rgb), 0.42)",
                      background: "linear-gradient(180deg, rgba(var(--brand-primary-rgb), 0.22), rgba(24, 24, 27, 0.98) 55%, rgba(15, 23, 42, 0.98))",
                      boxShadow: "0 20px 56px rgba(0, 0, 0, 0.3)",
                    }
                  : {
                      borderColor: "rgba(255, 255, 255, 0.08)",
                      backgroundColor: "#18181b",
                      boxShadow: "0 20px 56px rgba(0, 0, 0, 0.24)",
                    }
                : undefined;
              const titleStyle = isDarkMode ? { color: "#fafafa" } : undefined;
              const descriptionStyle = isDarkMode
                ? { color: isGrowthPlan ? "rgba(240, 253, 244, 0.82)" : "#d4d4d8" }
                : undefined;
              const priceStyle = isDarkMode ? { color: "#fafafa" } : undefined;
              const featureStyle = isDarkMode
                ? { color: isGrowthPlan ? "rgba(244, 255, 247, 0.88)" : "#e4e4e7" }
                : undefined;
              const badgeStyle = isDarkMode
                ? {
                    backgroundColor: isGrowthPlan ? "rgba(220, 252, 231, 0.12)" : "rgba(var(--brand-primary-rgb), 0.12)",
                    color: isGrowthPlan ? "#bbf7d0" : "#d1fae5",
                  }
                : undefined;

              return (
                <div
                  key={plan.slug}
                  className={`rounded-[28px] border p-6 shadow-[0_18px_48px_rgba(15,23,42,0.08)] ${plan.slug === "growth" ? "border-[var(--brand-primary)] bg-[linear-gradient(180deg,rgba(227,244,239,0.9),#ffffff)]" : "border-[#eaecf0] bg-white"}`}
                  style={cardStyle}
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h4 className="text-2xl font-extrabold tracking-[-0.03em] text-[#101828]" style={titleStyle}>{plan.name}</h4>
                      {plan.badge ? (
                        <span className="rounded-full bg-[var(--brand-primary)]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--brand-primary)]" style={badgeStyle}>
                          {plan.badge}
                        </span>
                      ) : null}
                    </div>
                    <p className={`mt-2 text-sm text-[#667085] ${isGrowthPlan ? "lg:whitespace-nowrap" : ""}`.trim()} style={descriptionStyle}>{plan.description}</p>
                  </div>

                  <p className="mt-6 text-4xl font-extrabold tracking-[-0.05em] text-[#101828]" style={priceStyle}>{plan.price}</p>

                  <ul className="mt-6 space-y-3 text-sm text-[#344054]" style={featureStyle}>
                    {plan.features.map(feature => (
                      <li key={feature} className="flex items-start gap-3">
                        <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--brand-primary)]/10 text-[12px] font-bold text-[var(--brand-primary)]" style={isDarkMode ? { backgroundColor: "rgba(var(--brand-primary-rgb), 0.16)", color: "#d1fae5" } : undefined}>✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => {
                      void handleOpenBillingCheckout(plan.slug);
                    }}
                    disabled={Boolean(isOpeningBillingCheckoutPlan) || isCurrentPlan}
                    aria-busy={isOpening && !isCurrentPlan}
                    className={`mt-8 w-full rounded-2xl px-5 py-3 text-sm font-semibold transition ${plan.slug === "growth" ? "brand-primary-button" : "brand-primary-text bg-[#f8fafc] ring-1 ring-[#d0d5dd] hover:bg-[#f2f4f7]"} disabled:cursor-wait disabled:opacity-70`}
                    style={isDarkMode && !isGrowthPlan ? {
                      backgroundColor: "#27272a",
                      borderColor: "rgba(255, 255, 255, 0.1)",
                      color: "#f4f4f5",
                    } : undefined}
                  >
                    {isCurrentPlan
                      ? copy.billing.upgradeModal.currentPlan
                      : isOpening
                        ? hasPaidBillingPlan ? copy.billing.changingPlan : copy.billing.openingCheckout
                        : hasPaidBillingPlan ? copy.billing.upgradeModal.switchPlan(plan.name) : copy.billing.upgradeModal.selectPlan(plan.name)}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </AnimatedModal>

      {section ? (
      <div className="columns-1 gap-6 lg:columns-2 [&>*]:mb-6 [&>*]:break-inside-avoid">
          {section === "ticketing" && canManageAdminSettings ? (
          <>
          <SettingsCard
            eyebrow={copy.categories.eyebrow}
            title={copy.categories.title}
            description={copy.categories.description}
            actions={
              <div className="shrink-0 self-start">
                {isLoadingSettings ? (
                  <CounterBadge label={copy.categories.countLabel} />
                ) : (
                  <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.categories.count(ticketCategories.length)}</span>
                )}
              </div>
            }
          >
            <div className="space-y-4 rounded-[22px] border border-[#eaecf0] bg-white px-5 py-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.categories.manageAction}</p>
                  <p className="mt-2 text-sm text-[#667085]">{copy.categories.managerModal.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsTicketCategoryManagerOpen(true)}
                  disabled={isLoadingSettings}
                  className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {copy.categories.manageAction}
                </button>
              </div>

              {isLoadingSettings ? (
                <LoadingStateCard label={copy.categories.loading} />
              ) : ticketCategories.length === 0 ? (
                <div className="rounded-[22px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-6 text-center">
                  <p className="text-sm font-semibold text-[#101828]">{copy.categories.noCategoriesTitle}</p>
                  <p className="mt-2 text-sm text-[#667085]">{copy.categories.noCategoriesDescription}</p>
                </div>
              ) : (
                <div className="mt-1 flex flex-wrap gap-3">
                  {categoryUsageSummary.preview.map(category => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => {
                        setIsTicketCategoryManagerOpen(true);
                        handleEditTicketCategory(category);
                      }}
                      className="min-w-[13rem] rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3 text-left transition-colors hover:border-[var(--brand-primary)] hover:bg-[var(--brand-primary-soft)]"
                    >
                      <p className="truncate text-sm font-semibold text-[#101828]">{category.name}</p>
                      <p className="mt-1 text-xs text-[#667085]">{copy.categories.customFieldsCount(category.customFields.length)}</p>
                    </button>
                  ))}
                  {ticketCategories.length > categoryUsageSummary.preview.length ? (
                    <button
                      type="button"
                      onClick={() => setIsTicketCategoryManagerOpen(true)}
                      className="rounded-[18px] border border-dashed border-[#d0d5dd] bg-white px-4 py-3 text-sm font-semibold text-[#475467] transition-colors hover:border-[var(--brand-primary)] hover:text-[#101828]"
                    >
                      +{ticketCategories.length - categoryUsageSummary.preview.length}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </SettingsCard>

          <SettingsCard
            eyebrow={copy.emailIntake.eyebrow}
            title={copy.emailIntake.title}
            description={copy.emailIntake.description}
            actions={
              <div className="shrink-0 self-start">
                {isLoadingSettings ? (
                  <CounterBadge label={copy.emailIntake.countLabel} />
                ) : (
                  <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.emailIntake.count(caseEmailRouteDrafts.length)}</span>
                )}
              </div>
            }
          >
            <div className="space-y-4">
              {!tenantEmailDomain ? (
                <div className="rounded-[22px] border border-[#fecdca] bg-[#fff7ed] px-5 py-4">
                  <p className="text-sm font-semibold text-[#b54708]">{copy.emailIntake.tenantHostnameUnavailable}</p>
                  <p className="mt-1 text-sm text-[#7a2e0e]">{copy.emailIntake.tenantHostnameUnavailableDetail}</p>
                </div>
              ) : null}

              <div className="space-y-4 rounded-[22px] border border-[#eaecf0] bg-white px-5 py-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.manageAction}</p>
                    <p className="mt-2 text-sm text-[#667085]">{copy.emailIntake.managerModal.description}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsMailboxManagerOpen(true)}
                    disabled={isLoadingSettings}
                    className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {copy.emailIntake.manageAction}
                  </button>
                </div>

                {isLoadingSettings ? (
                  <LoadingStateCard label={copy.emailIntake.loading} />
                ) : caseEmailRouteDrafts.length === 0 ? (
                  <div className="rounded-[22px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-6 text-center">
                    <p className="text-sm font-semibold text-[#101828]">{copy.emailIntake.noMailboxesTitle}</p>
                    <p className="mt-2 text-sm text-[#667085]">{copy.emailIntake.noMailboxesDescription}</p>
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {mailboxSummaryPreview.map(route => {
                      const managedMailboxAddress = buildManagedSupportAddress(route.mailbox, tenantMailboxDomain) || `${route.mailbox || "help"}@${tenantMailboxDomain ?? "workspace.mail.kuii.net"}`;
                      const persistedRoute = workspaceSettings.caseEmailRoutes.find(existingRoute => existingRoute.id === route.id) ?? null;
                      const outboundEnabled = Boolean(persistedRoute?.outboundEnabled);
                      const categoryLabel = route.categoryId
                        ? ticketCategories.find(category => category.id === route.categoryId)?.name ?? copy.emailIntake.noDefaultCategory
                        : copy.emailIntake.noDefaultCategory;
                      const itemLabel = route.itemId
                        ? itemNameById.get(route.itemId) ?? copy.emailIntake.noDefaultItem
                        : copy.emailIntake.noDefaultItem;

                      return (
                        <button
                          key={route.id}
                          type="button"
                          onClick={() => setIsMailboxManagerOpen(true)}
                          className="rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-4 text-left transition-colors hover:border-[var(--brand-primary)] hover:bg-[var(--brand-primary-soft)]"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-[#101828]">{managedMailboxAddress}</p>
                              <div className="mt-1 flex flex-wrap gap-2 text-xs text-[#667085]">
                                <span>{copy.emailIntake.defaultCategory}: {categoryLabel}</span>
                                <span>{copy.emailIntake.defaultItem}: {itemLabel}</span>
                              </div>
                            </div>
                            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${outboundEnabled ? "bg-[#ecfdf3] text-[#027a48]" : "bg-[#f3f4f6] text-[#475467]"}`}>
                              {outboundEnabled ? copy.emailIntake.outboundEnabledBadge : copy.emailIntake.outboundDisabledBadge}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {caseEmailRouteDrafts.length > mailboxSummaryPreview.length ? (
                  <button
                    type="button"
                    onClick={() => setIsMailboxManagerOpen(true)}
                    className="rounded-[18px] border border-dashed border-[#d0d5dd] bg-white px-4 py-3 text-sm font-semibold text-[#475467] transition-colors hover:border-[var(--brand-primary)] hover:text-[#101828]"
                  >
                    +{caseEmailRouteDrafts.length - mailboxSummaryPreview.length}
                  </button>
                ) : null}
              </div>

              {caseEmailRouteError ? <p className="text-sm font-semibold text-[#b42318]">{caseEmailRouteError}</p> : null}
            </div>
          </SettingsCard>

          <SettingsCard
            eyebrow={copy.notifications.eyebrow}
            title={copy.notifications.title}
            description={(
              <>
                {copy.notifications.descriptionPrefix}{" "}
                <strong className="font-semibold text-[#101828]">{copy.notifications.descriptionSender}</strong>.
              </>
            )}
            actions={
              <div className="shrink-0 self-start">
                {isLoadingSettings ? (
                  <CounterBadge label={copy.notifications.countLabel} />
                ) : (
                  <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.notifications.count(enabledNotificationCount)}</span>
                )}
              </div>
            }
          >
            <div className="space-y-4 rounded-[22px] border border-[#eaecf0] bg-white px-5 py-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.notifications.modal.title}</p>
                  <p className="mt-2 text-sm text-[#667085]">{copy.notifications.modal.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsNotificationSettingsModalOpen(true)}
                  disabled={isLoadingSettings}
                  className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {copy.notifications.modal.openAction}
                </button>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {notificationSummaryPreview.map(eventKey => {
                  const eventCopy = copy.notifications.events[eventKey];
                  const rule = normalizedNotificationSettingsDraft[eventKey];
                  const selectedCategoryNames = rule.categoryIds
                    .map(categoryId => ticketCategories.find(category => category.id === categoryId)?.name)
                    .filter((name): name is string => Boolean(name));

                  return (
                    <button
                      key={eventKey}
                      type="button"
                      onClick={() => setIsNotificationSettingsModalOpen(true)}
                      className="rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-4 text-left transition-colors hover:border-[var(--brand-primary)] hover:bg-[var(--brand-primary-soft)]"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-[#101828]">{eventCopy.title}</p>
                          <div className="mt-1 flex flex-wrap gap-2 text-xs text-[#667085]">
                            <span>
                              {selectedCategoryNames.length > 0
                                ? copy.notifications.selectedCategories(selectedCategoryNames.join(", "))
                                : copy.notifications.allCategories}
                            </span>
                          </div>
                        </div>
                        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${rule.enabled ? "bg-[#ecfdf3] text-[#027a48]" : "bg-[#f3f4f6] text-[#475467]"}`}>
                          {rule.enabled ? copy.common.on : copy.common.off}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {notificationEventOrder.length > notificationSummaryPreview.length ? (
                <button
                  type="button"
                  onClick={() => setIsNotificationSettingsModalOpen(true)}
                  className="rounded-[18px] border border-dashed border-[#d0d5dd] bg-white px-4 py-3 text-sm font-semibold text-[#475467] transition-colors hover:border-[var(--brand-primary)] hover:text-[#101828]"
                >
                  +{notificationEventOrder.length - notificationSummaryPreview.length}
                </button>
              ) : null}
            </div>
          </SettingsCard>

          <AnimatedModal
            open={canManageAdminSettings && isNotificationSettingsModalOpen}
            onClose={() => setIsNotificationSettingsModalOpen(false)}
            backdropAriaLabel={copy.notifications.modal.closeAria}
            overlayClassName="fixed inset-0 z-[80] bg-[#101828]/35 backdrop-blur-[6px]"
            overlayStyle={portalBrandStyle}
            dialogClassName="app-modal-dialog relative max-h-[calc(100vh-4rem)] w-full max-w-4xl overflow-y-auto rounded-[32px] border border-white/70 bg-[#fcfcfd] p-6 shadow-[0_32px_100px_rgba(15,23,42,0.24)] custom-scrollbar"
          >
            <button
              type="button"
              onClick={() => setIsNotificationSettingsModalOpen(false)}
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
            >
              ×
            </button>

            <div className="flex flex-wrap items-start justify-between gap-4 pr-16">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.notifications.modal.eyebrow}</p>
                <h3 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.notifications.modal.title}</h3>
                <p className="mt-2 max-w-3xl text-sm text-[#667085]">{copy.notifications.modal.description}</p>
              </div>
            </div>

            <div className="mt-6 space-y-4 rounded-[26px] border border-[#eaecf0] bg-[#fcfcfd] p-5 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
              {notificationEventOrder.map(eventKey => {
                const eventCopy = copy.notifications.events[eventKey];
                const rule = normalizedNotificationSettingsDraft[eventKey];
                const selectedCategoryNames = rule.categoryIds
                  .map(categoryId => ticketCategories.find(category => category.id === categoryId)?.name)
                  .filter((name): name is string => Boolean(name));

                return (
                  <div key={eventKey} className="rounded-[20px] border border-[#eaecf0] bg-white px-5 py-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-[#101828]">{eventCopy.title}</p>
                        <p className="mt-2 text-sm text-[#667085]">{eventCopy.description}</p>
                        <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.notifications.categoriesLabel}</p>
                        <p className="mt-1 text-sm text-[#667085]">
                          {selectedCategoryNames.length > 0
                            ? copy.notifications.selectedCategories(selectedCategoryNames.join(", "))
                            : copy.notifications.allCategories}
                        </p>
                      </div>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={rule.enabled}
                        aria-label={eventCopy.toggleAria}
                        onClick={() => handleToggleWorkspaceNotificationRule(eventKey)}
                        className="inline-flex items-center gap-3 rounded-full bg-[#f3f4f6] px-3 py-2 text-sm font-semibold text-[#344054] transition-colors hover:bg-[#eaecf0]"
                      >
                        <span>{rule.enabled ? copy.common.on : copy.common.off}</span>
                        <span
                          aria-hidden="true"
                          className={`relative h-7 w-12 rounded-full transition-colors ${rule.enabled ? "bg-[var(--brand-primary)]" : "bg-[#d0d5dd]"}`}
                        >
                          <span
                            className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-[0_2px_8px_rgba(15,23,42,0.18)] transition-all ${rule.enabled ? "left-6" : "left-1"}`}
                          />
                        </span>
                      </button>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {ticketCategories.map(category => {
                        const isSelected = rule.categoryIds.includes(category.id);

                        return (
                          <button
                            key={category.id}
                            type="button"
                            onClick={() => handleToggleWorkspaceNotificationCategory(eventKey, category.id)}
                            className={`rounded-full px-3 py-2 text-xs font-semibold transition-all ${isSelected ? "bg-[var(--brand-primary-soft)] text-[var(--brand-primary)] shadow-[0_10px_22px_rgba(var(--brand-primary-rgb),0.08)] ring-1 ring-[var(--brand-primary-ring)]" : "bg-[#f8fafc] text-[#475467] ring-1 ring-[#eaecf0] hover:bg-[#eef2f6]"}`}
                          >
                            {category.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="flex flex-wrap items-center justify-end gap-3 rounded-[20px] bg-white px-4 py-3">
                <button
                  type="button"
                  onClick={handleResetWorkspaceNotifications}
                  disabled={isLoadingSettings || !hasPendingNotificationChanges}
                  className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {copy.common.reset}
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveWorkspaceNotifications(true)}
                  disabled={isLoadingSettings || !hasPendingNotificationChanges}
                  className={`rounded-xl px-5 py-2.5 text-sm font-bold transition-all ${
                    !isLoadingSettings && hasPendingNotificationChanges
                      ? "brand-primary-button"
                      : "cursor-not-allowed bg-[#f2f4f7] text-[#98a2b3]"
                  }`}
                >
                  {copy.notifications.saveAction}
                </button>
              </div>
            </div>
          </AnimatedModal>

          <AnimatedModal
            open={canManageAdminSettings && isTicketCategoryManagerOpen}
            onClose={() => setIsTicketCategoryManagerOpen(false)}
            backdropAriaLabel={copy.categories.managerModal.closeAria}
            overlayClassName="fixed inset-0 z-[80] bg-[#101828]/35 backdrop-blur-[6px]"
            overlayStyle={portalBrandStyle}
            dialogClassName="app-modal-dialog relative max-h-[calc(100vh-4rem)] w-full max-w-[min(96vw,1320px)] overflow-y-auto rounded-[32px] border border-white/70 bg-[#fcfcfd] p-6 shadow-[0_32px_100px_rgba(15,23,42,0.24)] custom-scrollbar"
          >
            <button
              type="button"
              onClick={() => setIsTicketCategoryManagerOpen(false)}
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
            >
              ×
            </button>

            <div className="flex flex-wrap items-start justify-between gap-4 pr-16">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.categories.managerModal.eyebrow}</p>
                <h3 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.categories.title}</h3>
                <p className="mt-2 max-w-3xl text-sm text-[#667085]">{copy.categories.managerModal.description}</p>
              </div>
            </div>

            <div className="mt-6 space-y-4 rounded-[26px] border border-[#eaecf0] bg-white p-5 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
              <div className="flex flex-wrap items-center gap-3">
                {isLoadingSettings ? (
                  <LoadingSpinnerBadge label={copy.categories.loading} />
                ) : (
                  <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.categories.count(ticketCategories.length)}</span>
                )}
                <SectionSearchInput
                  value={ticketCategorySearch}
                  onChange={setTicketCategorySearch}
                  placeholder={copy.categories.searchPlaceholder}
                  ariaLabel={copy.categories.searchAriaLabel}
                  disabled={isLoadingSettings}
                  className="min-w-[16rem] flex-1"
                />
                <button
                  type="button"
                  onClick={handleCreateTicketCategory}
                  disabled={isLoadingSettings}
                  className="brand-primary-button rounded-xl px-4 py-2.5 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {copy.categories.newCategory}
                </button>
              </div>

              <div className="overflow-hidden rounded-[26px] border border-[#eaecf0] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
                <div className="settings-table-head hidden grid-cols-[minmax(0,1.35fr)_120px_120px_120px] gap-3 bg-[#f9fafb] px-5 py-4 lg:grid">
                  {localizedTicketCategorySortHeaders.map(header => (
                    <button
                      key={header.key}
                      type="button"
                      onClick={() => handleTicketCategorySort(header.key)}
                      className="flex items-center gap-2 text-left text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3] transition-colors hover:text-[#667085]"
                      aria-label={copy.common.sortBy(header.label, ticketCategorySortColumn === header.key, ticketCategorySortDirection)}
                    >
                      <span>{header.label}</span>
                      <span className={`inline-flex items-center ${ticketCategorySortColumn === header.key ? "text-[#667085]" : "text-slate-300"}`} aria-hidden="true">
                        {getSortIndicator(ticketCategorySortColumn, ticketCategorySortDirection, header.key)}
                      </span>
                    </button>
                  ))}
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.common.action}</span>
                </div>

                <div className="settings-table-body">
                  {isLoadingSettings ? (
                    <div className="px-5 py-8">
                      <LoadingStateCard label={copy.categories.loading} />
                    </div>
                  ) : filteredTicketCategories.length === 0 ? (
                    <div className="px-5 py-8 text-center">
                      <p className="text-sm font-semibold text-[#101828]">{copy.categories.emptySearchTitle}</p>
                      <p className="mt-2 text-sm text-[#667085]">{copy.categories.emptySearchDescription}</p>
                    </div>
                  ) : filteredTicketCategories.map(category => {
                    const ticketUsageCount = getTicketCategoryTicketUsage(category.name);
                    const articleUsageCount = getTicketCategoryArticleUsage(category.name);
                    const usageCount = ticketUsageCount + articleUsageCount;

                    return (
                      <div
                        key={category.id}
                        className="settings-table-row grid gap-3 bg-white px-5 py-5 transition-colors hover:bg-[var(--brand-primary-soft)] lg:grid-cols-[minmax(0,1.35fr)_120px_120px_120px]"
                      >
                        <div className="min-w-0">
                          <h3 className="font-headline text-xl font-extrabold tracking-[-0.04em] text-[#101828]">{category.name}</h3>
                          <p className="mt-2 text-sm text-[#667085]">{category.description || copy.common.noDescriptionYet}</p>
                          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">
                            {copy.categories.customFieldsCount(category.customFields.length)}
                          </p>
                        </div>

                        <div>
                          <span className="inline-flex rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">
                            {copy.categories.linkedCount(usageCount)}
                          </span>
                          <p className="mt-2 text-xs text-[#667085]">{copy.categories.ticketsAndArticles(ticketUsageCount, articleUsageCount)}</p>
                        </div>

                        <div>
                          <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold ${usageCount > 0 ? "bg-[#effaf5] text-[#027a48]" : "bg-[#f3f4f6] text-[#475467]"}`}>
                            {usageCount > 0 ? copy.categories.inUse : copy.categories.ready}
                          </span>
                        </div>

                        <div className="flex items-start justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditTicketCategory(category)}
                            className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                          >
                            {copy.common.edit}
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteTicketCategory(category)}
                            className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#b42318] ring-1 ring-[#f3d0cb] transition-colors hover:bg-[#fef3f2]"
                          >
                            {copy.common.delete}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsTicketCategoryManagerOpen(false)}
                  className="rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                >
                  {copy.common.done}
                </button>
              </div>
            </div>
          </AnimatedModal>

          <AnimatedModal
            open={canManageAdminSettings && isMailboxManagerOpen}
            onClose={() => setIsMailboxManagerOpen(false)}
            backdropAriaLabel={copy.emailIntake.managerModal.closeAria}
            overlayClassName="fixed inset-0 z-[80] bg-[#101828]/35 backdrop-blur-[6px]"
            overlayStyle={portalBrandStyle}
            dialogClassName="app-modal-dialog relative flex max-h-[calc(100dvh-2rem)] w-full max-w-[min(96vw,1440px)] flex-col overflow-hidden rounded-[32px] border border-white/70 bg-[#fcfcfd] p-4 shadow-[0_32px_100px_rgba(15,23,42,0.24)] sm:max-h-[calc(100dvh-4rem)] sm:p-6"
          >
            <button
              type="button"
              onClick={() => setIsMailboxManagerOpen(false)}
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
            >
              ×
            </button>

            <div className="flex shrink-0 flex-wrap items-start justify-between gap-4 pr-16">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.emailIntake.managerModal.eyebrow}</p>
                <h3 className="mt-2 font-headline text-2xl font-extrabold tracking-normal text-[#101828]">{copy.emailIntake.title}</h3>
                <p className="mt-2 max-w-3xl text-sm text-[#667085]">{copy.emailIntake.managerModal.description}</p>
              </div>
            </div>

            <div className="mt-4 flex min-h-0 flex-col gap-4">
              <div className="flex shrink-0 flex-wrap items-center gap-3">
                {isLoadingSettings ? (
                  <LoadingSpinnerBadge label={copy.emailIntake.loading} />
                ) : (
                  <span className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#475467]">{copy.emailIntake.count(caseEmailRouteDrafts.length)}</span>
                )}
                <SectionSearchInput
                  value={caseEmailRouteSearch}
                  onChange={setCaseEmailRouteSearch}
                  placeholder={copy.emailIntake.searchPlaceholder}
                  ariaLabel={copy.emailIntake.searchAriaLabel}
                  disabled={isLoadingSettings}
                  className="min-w-0 basis-full sm:basis-auto sm:flex-1"
                />
                <span className={`rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] ${emailRoutingSyncBadgeClassName}`}>{emailRoutingSyncBadgeLabel}</span>
                <button
                  type="button"
                  onClick={() => {
                    handleAddCaseEmailRoute();
                  }}
                  disabled={isLoadingSettings}
                  className="brand-primary-button rounded-xl px-4 py-2.5 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {copy.emailIntake.addRoute}
                </button>
              </div>

              <div ref={caseEmailRouteListRef} className="custom-scrollbar min-h-0 space-y-4 overflow-y-auto overscroll-contain">
              {!tenantEmailDomain ? (
                <div className="rounded-[22px] border border-[#fecdca] bg-[#fff7ed] px-5 py-4">
                  <p className="text-sm font-semibold text-[#b54708]">{copy.emailIntake.tenantHostnameUnavailable}</p>
                  <p className="mt-1 text-sm text-[#7a2e0e]">{copy.emailIntake.tenantHostnameUnavailableDetail}</p>
                </div>
              ) : null}

              {isLoadingSettings ? (
                <LoadingStateCard label={copy.emailIntake.loading} />
              ) : caseEmailRouteDrafts.length > 0 ? (
                filteredCaseEmailRouteDrafts.length > 0 ? (
                  filteredCaseEmailRouteDrafts.map(route => (
                  <div key={route.id} className="rounded-[22px] border border-[#eaecf0] bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
                    {(() => {
                      const persistedRoute = workspaceSettings.caseEmailRoutes.find(existingRoute => existingRoute.id === route.id) ?? null;
                      const outboundEnabled = Boolean(persistedRoute?.outboundEnabled);
                      const outboundStatus = persistedRoute?.outboundVerificationStatus ?? (outboundEnabled ? "pending" : "disabled");
                      const isBusy = caseEmailRouteOutboundBusyId === route.id;
                      const isRefreshBusy = caseEmailRouteRefreshBusyId === route.id;
                      const showInlineOutboundBusyMessage = caseEmailRouteFeedbackId === route.id
                        && Boolean(caseEmailRouteOutboundMessage)
                        && !caseEmailRouteOutboundError
                        && (isBusy || isRefreshBusy);
                      const hasMailboxName = route.mailbox.trim().length > 0;
                      const isRouteDirty = isCaseEmailRouteDirty(route.id);
                      const canSaveRoute = isRouteDirty && Boolean(tenantMailboxDomain);
                      const canToggleOutbound = Boolean(persistedRoute) && hasMailboxName && !isRouteDirty && !isBusy;
                      const canRefreshOutboundStatus = Boolean(persistedRoute?.outboundEnabled) && !isRouteDirty && !isBusy && !isRefreshBusy;
                      const outboundSender = getRouteOutboundSender(route, tenantMailboxDomain);
                      const hasCustomOutboundSender = route.outboundFromEmail.trim().length > 0;
                      const managedMailboxAddress = buildManagedSupportAddress(route.mailbox, tenantMailboxDomain) || `help@${tenantMailboxDomain ?? "workspace.mail.kuii.net"}`;

                      return (
                        <>
                          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#eaecf0] pb-4">
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.emailIntake.mailboxName}</p>
                              <p className="mt-2 break-all text-lg font-extrabold tracking-normal text-[#101828]">{managedMailboxAddress}</p>
                              <p className="mt-1 text-xs text-[#667085]">{hasCustomOutboundSender ? copy.emailIntake.outboundRouteSummary(outboundSender) : copy.emailIntake.outboundRouteSummary(managedMailboxAddress)}</p>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleRefreshCaseEmailRouteStatus(route.id)}
                                disabled={!canRefreshOutboundStatus}
                                className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {isRefreshBusy ? copy.emailIntake.refreshingAction : copy.emailIntake.refreshAction}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteCaseEmailRoute(route.id)}
                                className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#b42318] ring-1 ring-[#f3d0cb] transition-colors hover:bg-[#fef3f2]"
                              >
                                {copy.common.delete}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSaveCaseEmailRoute(route.id)}
                                disabled={!canSaveRoute}
                                className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                                  canSaveRoute
                                    ? "brand-primary-button"
                                    : "cursor-not-allowed bg-[#f2f4f7] text-[#98a2b3]"
                                }`}
                              >
                                {copy.emailIntake.saveRoute}
                              </button>
                            </div>
                          </div>

                          <div className="mt-4 space-y-4">
                            <div className="grid gap-4 md:grid-cols-3">
                              <label className="block rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.mailboxName}</span>
                                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm font-semibold text-[#101828]">
                                  <StableTextInput
                                    value={route.mailbox}
                                    onChange={nextValue => handleCaseEmailRouteDraftChange(route.id, "mailbox", nextValue)}
                                    placeholder="help"
                                    className="min-w-0 flex-1 border-none bg-transparent p-0 outline-none placeholder:text-[#98a2b3]"
                                  />
                                  <span className="min-w-0 break-all text-[#667085]">@{tenantMailboxDomain ?? "workspace.mail.kuii.net"}</span>
                                </div>
                              </label>

                              <label className="block rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.defaultCategory}</span>
                                <select
                                  value={route.categoryId}
                                  onChange={event => handleCaseEmailRouteDraftChange(route.id, "categoryId", event.target.value)}
                                  className="mt-3 w-full border-none bg-transparent p-0 text-sm font-semibold text-[#101828] outline-none"
                                >
                                  <option value="">{copy.emailIntake.noDefaultCategory}</option>
                                  {ticketCategories.map(category => (
                                    <option key={category.id} value={category.id}>
                                      {category.name}
                                    </option>
                                  ))}
                                </select>
                              </label>

                              <label className="block rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.defaultItem}</span>
                                <select
                                  value={route.itemId}
                                  onChange={event => handleCaseEmailRouteDraftChange(route.id, "itemId", event.target.value)}
                                  className="mt-3 w-full border-none bg-transparent p-0 text-sm font-semibold text-[#101828] outline-none"
                                >
                                  <option value="">{copy.emailIntake.noDefaultItem}</option>
                                  {productPlans.map(plan => (
                                    <option key={plan.id} value={plan.id}>
                                      {plan.name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </div>

                            {outboundEnabled ? (
                              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(260px,0.9fr)]">
                                <label className="block rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.outboundFromLabel}</span>
                                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${outboundStatus === "ready" ? "bg-[#ecfdf3] text-[#027a48]" : "bg-[#eff8ff] text-[#175cd3]"}`}>
                                      {outboundStatus === "ready"
                                        ? copy.emailIntake.outboundVerifiedBadge
                                        : copy.emailIntake.outboundPendingBadge}
                                    </span>
                                  </div>
                                  <StableTextInput
                                    value={route.outboundFromEmail}
                                    onChange={nextValue => handleCaseEmailRouteDraftChange(route.id, "outboundFromEmail", nextValue)}
                                    inputMode="email"
                                    autoComplete="email"
                                    placeholder={copy.emailIntake.outboundFromPlaceholder}
                                    className="mt-3 w-full border-none bg-transparent p-0 text-sm font-semibold text-[#101828] outline-none placeholder:text-[#98a2b3]"
                                  />
                                  <p className="mt-2 text-xs leading-5 text-[#667085]">{copy.emailIntake.outboundFromHint}</p>
                                </label>
                                <div className="rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3">
                                  <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.outbound}</p>
                                  </div>
                                  {hasCustomOutboundSender && normalizeRouteOutboundFromEmail(route.outboundFromEmail) !== normalizeRouteOutboundFromEmail(persistedRoute?.outboundFromEmail) ? (
                                    <p className="mt-3 text-xs leading-5 text-[#667085]">{copy.emailIntake.customOutboundSaveHint}</p>
                                  ) : !persistedRoute || !hasMailboxName ? (
                                    <p className="mt-3 text-xs leading-5 text-[#667085]">{copy.emailIntake.saveBeforeOutbound}</p>
                                  ) : hasCustomOutboundSender && outboundStatus !== "ready" ? (
                                    <p className="mt-3 text-xs leading-5 text-[#667085]">{copy.emailIntake.customOutboundPendingHint(outboundSender)}</p>
                                  ) : (
                                    <p className="mt-3 text-xs leading-5 text-[#667085]">{copy.emailIntake.outboundRouteSummary(outboundSender)}</p>
                                  )}
                                  <div className="mt-3 flex flex-wrap items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleOpenCaseEmailTemplateModal(route.id)}
                                      className="rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                                    >
                                      {copy.emailIntake.templateButton}
                                    </button>
                                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${normalizeOutboundTemplateHtml(route.templateHtml) ? "bg-[#eff8ff] text-[#175cd3]" : "bg-[#f3f4f6] text-[#475467]"}`}>
                                      {normalizeOutboundTemplateHtml(route.templateHtml)
                                        ? copy.emailIntake.templateCustomBadge
                                        : copy.emailIntake.templateDefaultBadge}
                                    </span>
                                  </div>
                                  {persistedRoute?.outboundVerificationError ? <p className="mt-2 text-xs font-semibold text-[#b42318]">{persistedRoute.outboundVerificationError}</p> : null}
                                </div>
                              </div>
                            ) : null}
                          </div>

                          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[#eaecf0] pt-4">
                            <label className={`inline-flex items-center gap-2 rounded-full bg-[#f8fafc] px-3 py-2 text-xs font-semibold ${canToggleOutbound ? "cursor-pointer text-[#344054]" : "cursor-not-allowed text-[#98a2b3]"}`}>
                              <input
                                type="checkbox"
                                checked={outboundEnabled}
                                onChange={() => handleToggleRouteOutbound(route.id, !outboundEnabled)}
                                disabled={!canToggleOutbound}
                                className="h-4 w-4 rounded border-[#d0d5dd] text-[#006949] focus:ring-[#006949] disabled:cursor-not-allowed disabled:opacity-60"
                              />
                              <span>{copy.emailIntake.enableOutbound}</span>
                            </label>
                            <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${outboundEnabled ? "bg-[#ecfdf3] text-[#027a48]" : "bg-[#f3f4f6] text-[#475467]"}`}>
                              {isBusy
                                ? copy.emailIntake.syncBadge.syncing
                                : outboundEnabled
                                  ? copy.emailIntake.outboundEnabledBadge
                                  : copy.emailIntake.outboundDisabledBadge}
                            </span>
                            {showInlineOutboundBusyMessage ? (
                              <p className="ml-auto text-right text-xs font-semibold text-[#175cd3]">
                                {caseEmailRouteOutboundMessage}
                              </p>
                            ) : null}
                          </div>

                          {caseEmailRouteFeedbackId === route.id && ((caseEmailRouteOutboundMessage && !showInlineOutboundBusyMessage) || caseEmailRouteOutboundError) ? (
                            <div className="mt-3 flex justify-end">
                              <p className={`text-right text-xs font-semibold ${caseEmailRouteOutboundError ? "text-[#b42318]" : "text-[#175cd3]"}`}>
                                {caseEmailRouteOutboundError || caseEmailRouteOutboundMessage}
                              </p>
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                  ))
                ) : (
                  <div className="rounded-[22px] border border-dashed border-[#d0d5dd] bg-white px-5 py-6 text-center">
                    <p className="text-sm font-semibold text-[#101828]">{copy.emailIntake.emptySearchTitle}</p>
                    <p className="mt-2 text-sm text-[#667085]">{copy.emailIntake.emptySearchDescription}</p>
                  </div>
                )
              ) : (
                <div className="rounded-[22px] border border-dashed border-[#d0d5dd] bg-white px-5 py-6 text-center">
                  <p className="text-sm font-semibold text-[#101828]">{copy.emailIntake.noMailboxesTitle}</p>
                  <p className="mt-2 text-sm text-[#667085]">{copy.emailIntake.noMailboxesDescription}</p>
                </div>
              )}

              </div>

              <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[#eaecf0] pt-4">
                <div>
                  <p className={`mt-2 text-sm font-semibold ${emailRoutingSyncState === "error" ? "text-[#b42318]" : emailRoutingSyncState === "success" ? "text-[#027a48]" : emailRoutingSyncState === "syncing" ? "text-[#175cd3]" : "text-[#475467]"}`}>{localizedEmailRoutingSyncMessage}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleResetCaseEmailRoutes}
                    disabled={isLoadingSettings}
                    className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {copy.common.reset}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsMailboxManagerOpen(false)}
                    className="rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                  >
                    {copy.common.done}
                  </button>
                </div>
              </div>

              {caseEmailRouteError ? <p className="shrink-0 text-sm font-semibold text-[#b42318]">{caseEmailRouteError}</p> : null}
            </div>
          </AnimatedModal>

          <AnimatedModal
            open={canManageAdminSettings && Boolean(activeCaseEmailRouteTemplateDraft)}
            onClose={handleCloseCaseEmailTemplateModal}
            backdropAriaLabel={copy.emailIntake.templateModal.closeAria}
            overlayClassName="fixed inset-0 z-[90] bg-[#101828]/35 backdrop-blur-[6px]"
            overlayStyle={portalBrandStyle}
            dialogClassName="app-modal-dialog relative max-h-[calc(100vh-4rem)] w-full max-w-[min(96vw,1440px)] overflow-y-auto rounded-[32px] border border-white/70 bg-[#fcfcfd] p-6 shadow-[0_32px_100px_rgba(15,23,42,0.24)] custom-scrollbar"
          >
            {activeCaseEmailRouteTemplateDraft ? (
              <>
                <button
                  type="button"
                  onClick={handleCloseCaseEmailTemplateModal}
                  className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
                >
                  ×
                </button>

                <div className="flex flex-wrap items-start justify-between gap-4 pr-16">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.emailIntake.templateModal.eyebrow}</p>
                    <h3 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">
                      {copy.emailIntake.templateModal.title(buildManagedSupportAddress(activeCaseEmailRouteTemplateDraft.mailbox, tenantMailboxDomain) || activeCaseEmailRouteTemplateDraft.mailbox || copy.emailIntake.mailboxName)}
                    </h3>
                    <p className="mt-2 max-w-2xl text-sm text-[#667085]">{copy.emailIntake.templateModal.description}</p>
                  </div>
                </div>

                <div className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.85fr)]">
                  <div className="space-y-4">
                    <label className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4 block">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.templateModal.subjectLabel}</span>
                      <input
                        type="text"
                        value={activeCaseEmailTemplateEditor?.templateSubject ?? ""}
                        onChange={event => handleCaseEmailTemplateDraftChange(activeCaseEmailRouteTemplateDraft.id, { templateSubject: event.target.value })}
                        placeholder={copy.emailIntake.templateModal.subjectPlaceholder}
                        className="mt-3 w-full rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3 text-sm font-semibold text-[#101828] outline-none placeholder:text-[#98a2b3]"
                        spellCheck={false}
                      />
                      <p className="mt-3 text-sm text-[#667085]">{copy.emailIntake.templateModal.subjectHint}</p>
                    </label>

                    <label className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4 block">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.templateModal.htmlLabel}</span>
                      <p className="mt-1 text-sm text-[#667085]">{copy.emailIntake.templateModal.defaultHint}</p>

                      <div className="mt-3">
                        <HtmlCodeEditor
                          value={activeCaseEmailTemplateEditor?.templateHtml ?? ""}
                          onChange={value => handleCaseEmailTemplateDraftChange(activeCaseEmailRouteTemplateDraft.id, { templateHtml: value })}
                          placeholder={copy.emailIntake.templateModal.htmlPlaceholder}
                        />
                      </div>
                    </label>

                    <div className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.templateModal.previewTitle}</p>
                          <p className="mt-2 text-sm text-[#667085]">{copy.emailIntake.templateModal.previewDescription}</p>
                        </div>
                      </div>
                      <div className="mt-4 rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.emailIntake.templateModal.subjectLabel}</p>
                        <p className="mt-2 text-sm font-semibold text-[#101828]">{activeCaseEmailRoutePreviewSubject}</p>
                        <iframe
                          title="Outbound email template preview"
                          srcDoc={activeCaseEmailRoutePreviewHtml}
                          className="mt-4 h-[360px] w-full rounded-[16px] border border-[#eaecf0] bg-white"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.templateModal.mailboxLabel}</p>
                      <p className="mt-3 text-sm font-semibold text-[#101828]">
                        {buildManagedSupportAddress(activeCaseEmailRouteTemplateDraft.mailbox, tenantMailboxDomain) || copy.emailIntake.tenantHostnameUnavailable}
                      </p>
                    </div>

                    <div className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.emailIntake.templateModal.placeholdersTitle}</p>
                      <div className="mt-4 space-y-3">
                        {ticketUpdateEmailPlaceholderTokens.map(token => (
                          <div key={token} className="rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3">
                            <p className="font-mono text-[12px] font-semibold text-[#175cd3]">{`{{${token}}}`}</p>
                            <p className="mt-1 text-sm text-[#667085]">{copy.emailIntake.templateModal.placeholders[token]}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4 text-sm text-[#667085]">
                      <p className="mt-2">{copy.emailIntake.templateModal.saveHint}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleLoadDefaultCaseEmailSubject}
                      className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                    >
                      {copy.emailIntake.templateModal.useDefaultSubject}
                    </button>
                    <button
                      type="button"
                      onClick={handleLoadDefaultCaseEmailTemplate}
                      className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                    >
                      {copy.emailIntake.templateModal.useDefault}
                    </button>
                    <button
                      type="button"
                      onClick={handleClearCaseEmailTemplate}
                      className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                    >
                      {copy.emailIntake.templateModal.clearCustom}
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        if (handleSaveCaseEmailRoute(activeCaseEmailRouteTemplateDraft.id)) {
                          handleCloseCaseEmailTemplateModal();
                        }
                      }}
                      disabled={!isCaseEmailRouteDirty(activeCaseEmailRouteTemplateDraft.id) || !tenantMailboxDomain}
                      className={`rounded-xl px-5 py-2.5 text-sm font-bold transition-all ${
                        isCaseEmailRouteDirty(activeCaseEmailRouteTemplateDraft.id) && tenantMailboxDomain
                          ? "brand-primary-button"
                          : "cursor-not-allowed bg-[#f2f4f7] text-[#98a2b3]"
                      }`}
                    >
                      {copy.emailIntake.saveRoute}
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseCaseEmailTemplateModal}
                      className="rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                    >
                      {copy.emailIntake.templateModal.done}
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </AnimatedModal>
          </>
          ) : null}

          {section === "security" && canManageAdminSettings ? (
          <>
          <SettingsCard
            eyebrow={logsCopy.eyebrow}
            title={logsCopy.title}
            description={logsCopy.description}
          >
            <div className="space-y-5">
              <div className="rounded-[20px] border border-[#eaecf0] bg-white px-5 py-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{logsCopy.audit.title}</p>
                    <p className="mt-2 text-sm font-semibold text-[#101828]">{workspaceSettings.auditLogsEnabled ? logsViewCopy[language].states.enabled : logsViewCopy[language].states.disabled}</p>
                    <p className="mt-2 max-w-xl text-sm text-[#667085]">
                      {workspaceSettings.auditLogsEnabled
                        ? logsCopy.audit.enabledDetail
                        : logsCopy.audit.disabledDetail}
                    </p>
                    <p className="mt-2 max-w-xl text-sm text-[#667085]">
                      {workspaceSettings.auditLogRetentionDays
                        ? logsCopy.audit.retainedDetail(formatAccessLogRetentionDaysLabel(workspaceSettings.auditLogRetentionDays, language))
                        : logsCopy.audit.retainedUntilPolicyChanges}
                    </p>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={workspaceSettings.auditLogsEnabled}
                    aria-label={logsCopy.audit.toggleAria}
                    onClick={() => onUpdateWorkspaceSettings({
                      ...workspaceSettings,
                      auditLogsEnabled: !workspaceSettings.auditLogsEnabled,
                    })}
                    className="inline-flex items-center gap-3 rounded-full bg-[#f3f4f6] px-3 py-2 text-sm font-semibold text-[#344054] transition-colors hover:bg-[#eaecf0]"
                  >
                    <span>{workspaceSettings.auditLogsEnabled ? logsViewCopy[language].states.on : logsViewCopy[language].states.off}</span>
                    <span
                      aria-hidden="true"
                      className={`relative h-7 w-12 rounded-full transition-colors ${workspaceSettings.auditLogsEnabled ? "bg-[var(--brand-primary)]" : "bg-[#d0d5dd]"}`}
                    >
                      <span
                        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-[0_2px_8px_rgba(15,23,42,0.18)] transition-all ${workspaceSettings.auditLogsEnabled ? "left-6" : "left-1"}`}
                      />
                    </span>
                  </button>

                </div>

                <div className="mt-5 w-full">
                  <label className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]" htmlFor="workspace-audit-log-retention">
                    {logsCopy.audit.retentionLabel}
                  </label>
                  <select
                    id="workspace-audit-log-retention"
                    value={workspaceSettings.auditLogRetentionDays === null ? "" : String(workspaceSettings.auditLogRetentionDays)}
                    onChange={event => onUpdateWorkspaceSettings({
                      ...workspaceSettings,
                      auditLogRetentionDays: event.target.value ? Number(event.target.value) : null,
                    })}
                    className="brand-primary-focus-ring mt-3 w-full rounded-xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none"
                  >
                    {localizedAccessLogRetentionOptions.map(option => (
                      <option key={option.value || "none"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rounded-[20px] border border-[#eaecf0] bg-white px-5 py-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{logsCopy.access.title}</p>
                    <p className="mt-2 text-sm font-semibold text-[#101828]">{workspaceSettings.accessLogsEnabled ? logsViewCopy[language].states.enabled : logsViewCopy[language].states.disabled}</p>
                    <p className="mt-2 max-w-xl text-sm text-[#667085]">
                      {workspaceSettings.accessLogsEnabled
                        ? logsCopy.access.enabledDetail
                        : logsCopy.access.disabledDetail}
                    </p>
                    <p className="mt-2 max-w-xl text-sm text-[#667085]">
                      {workspaceSettings.accessLogRetentionDays
                        ? logsCopy.access.retainedDetail(formatAccessLogRetentionDaysLabel(workspaceSettings.accessLogRetentionDays, language))
                        : logsCopy.access.retainedUntilPolicyChanges}
                    </p>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={workspaceSettings.accessLogsEnabled}
                    aria-label={logsCopy.access.toggleAria}
                    onClick={() => onUpdateWorkspaceSettings({
                      ...workspaceSettings,
                      accessLogsEnabled: !workspaceSettings.accessLogsEnabled,
                    })}
                    className="inline-flex items-center gap-3 rounded-full bg-[#f3f4f6] px-3 py-2 text-sm font-semibold text-[#344054] transition-colors hover:bg-[#eaecf0]"
                  >
                    <span>{workspaceSettings.accessLogsEnabled ? logsViewCopy[language].states.on : logsViewCopy[language].states.off}</span>
                    <span
                      aria-hidden="true"
                      className={`relative h-7 w-12 rounded-full transition-colors ${workspaceSettings.accessLogsEnabled ? "bg-[var(--brand-primary)]" : "bg-[#d0d5dd]"}`}
                    >
                      <span
                        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-[0_2px_8px_rgba(15,23,42,0.18)] transition-all ${workspaceSettings.accessLogsEnabled ? "left-6" : "left-1"}`}
                      />
                    </span>
                  </button>

                </div>

                <div className="mt-5 w-full">
                  <label className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]" htmlFor="workspace-access-log-retention">
                    {logsCopy.access.retentionLabel}
                  </label>
                  <select
                    id="workspace-access-log-retention"
                    value={workspaceSettings.accessLogRetentionDays === null ? "" : String(workspaceSettings.accessLogRetentionDays)}
                    onChange={event => onUpdateWorkspaceSettings({
                      ...workspaceSettings,
                      accessLogRetentionDays: event.target.value ? Number(event.target.value) : null,
                    })}
                    className="brand-primary-focus-ring mt-3 w-full rounded-xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none"
                  >
                    {localizedAccessLogRetentionOptions.map(option => (
                      <option key={option.value || "none"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rounded-[20px] border border-[#eaecf0] bg-white px-5 py-5">
                <label className="block" htmlFor="workspace-log-text-language">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{logsCopy.logTextLanguage.label}</span>
                  <p className="mt-2 max-w-xl text-sm text-[#667085]">{logsCopy.logTextLanguage.description}</p>
                </label>
                <select
                  id="workspace-log-text-language"
                  value={workspaceSettings.logTextLanguage}
                  onChange={event => onUpdateWorkspaceSettings({
                    ...workspaceSettings,
                    logTextLanguage: event.target.value as AppLanguage,
                  })}
                  className="brand-primary-focus-ring mt-3 w-full rounded-xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none"
                >
                  <option value="en">{languageNames[language].en}</option>
                  <option value="ja">{languageNames[language].ja}</option>
                </select>
              </div>

              <div className="rounded-[18px] border border-[#eaecf0] bg-white px-4 py-4 text-sm text-[#667085]">
                <p className="font-semibold text-[#101828]">{logsCopy.retentionHelp.title}</p>
                <p className="mt-2">{logsCopy.retentionHelp.existingAndNew}</p>
                <p className="mt-2">{logsCopy.retentionHelp.disablingCapture}</p>
                <p className="mt-2">{logsCopy.retentionHelp.tenantStorage}</p>
              </div>
            </div>
          </SettingsCard>

          <div className="space-y-6">
          <SettingsCard
            eyebrow={copy.archiving.eyebrow}
            title={copy.archiving.title}
            description={copy.archiving.description}
          >
            <div className="space-y-5">
              <div className="rounded-[20px] border border-[#eaecf0] bg-white px-5 py-5">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.archiving.retentionLabel}</p>
                  <p className="mt-2 max-w-xl text-sm text-[#667085]">
                    {workspaceSettings.archiveRetentionMonths
                      ? copy.archiving.retentionDetail(formatArchiveRetentionMonthsLabel(workspaceSettings.archiveRetentionMonths, language))
                      : copy.archiving.noRetentionDetail}
                  </p>
                </div>

                <div className="mt-5 w-full">
                  <select
                    id="workspace-archive-retention-months"
                    value={workspaceSettings.archiveRetentionMonths === null ? "" : String(workspaceSettings.archiveRetentionMonths)}
                    onChange={event => onUpdateWorkspaceSettings({
                      ...workspaceSettings,
                      archiveRetentionMonths: event.target.value ? Number(event.target.value) : null,
                    })}
                    className="brand-primary-focus-ring w-full rounded-xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none"
                  >
                    {localizedArchiveRetentionOptions.map(option => (
                      <option key={option.value || "none"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rounded-[18px] border border-[#eaecf0] bg-white px-4 py-4 text-sm text-[#667085]">
                <p className="font-semibold text-[#101828]">{copy.archiving.helpTitle}</p>
                <p className="mt-2">{copy.archiving.helpExistingAndNew}</p>
                <p className="mt-2">{copy.archiving.helpAccess}</p>
                <p className="mt-2">{copy.archiving.helpScope}</p>
                <p className="mt-2">{copy.archiving.helpOverflow}</p>
              </div>
            </div>
          </SettingsCard>

          <SettingsCard
            eyebrow={copy.databaseRestore.eyebrow}
            title={copy.databaseRestore.title}
            description={copy.databaseRestore.description(restoreRetentionDays)}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-[#eaecf0] bg-white px-4 py-4">
              <div className="text-sm text-[#667085]">
                <p className="font-semibold text-[#101828]">{copy.databaseRestore.modalLauncherTitle}</p>
                <p className="mt-1">{copy.databaseRestore.modalLauncherDescription}</p>
              </div>
              <button
                type="button"
                onClick={openRestoreDatabaseModal}
                className="inline-flex items-center justify-center rounded-xl bg-[#b42318] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#912018]"
              >
                {copy.databaseRestore.restoreAction}
              </button>
            </div>

            {restoreDatabaseError ? (
              <div className="mt-4 rounded-[18px] border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                {restoreDatabaseError}
              </div>
            ) : null}

            {restoreDatabaseSuccess ? (
              <div className="mt-4 rounded-[18px] border border-[#abefc6] bg-[#ecfdf3] px-4 py-3 text-sm text-[#067647]">
                {restoreDatabaseSuccess}
              </div>
            ) : null}
          </SettingsCard>
          </div>

          <AnimatedModal
            open={canManageAdminSettings && isRestoreDatabaseModalOpen}
            onClose={closeRestoreDatabaseModal}
            backdropAriaLabel={copy.databaseRestore.modal.closeAria}
            overlayClassName="fixed inset-0 z-[90] bg-[#101828]/35 backdrop-blur-[6px]"
            overlayStyle={portalBrandStyle}
            dialogClassName="app-modal-dialog relative max-h-[calc(100vh-4rem)] w-full max-w-3xl overflow-y-auto rounded-[32px] border border-white/70 bg-[#fcfcfd] p-6 shadow-[0_32px_100px_rgba(15,23,42,0.24)] custom-scrollbar"
          >
            <button
              type="button"
              onClick={closeRestoreDatabaseModal}
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
            >
              ×
            </button>

            <div className="flex flex-wrap items-start justify-between gap-4 pr-16">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.databaseRestore.modal.eyebrow}</p>
                <h3 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.databaseRestore.title}</h3>
                <p className="mt-2 max-w-2xl text-sm text-[#667085]">{copy.databaseRestore.modal.description}</p>
              </div>
            </div>

            <div className="mt-6 space-y-4 rounded-[26px] border border-[#eaecf0] bg-[#fcfcfd] p-5 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
              <div className="settings-danger-warning rounded-[18px] border border-[#fecdca] bg-[#fef3f2] px-4 py-4 text-sm text-[#7a271a]">
                <p className="settings-danger-warning-title font-semibold text-[#b54708]">{copy.databaseRestore.warningTitle}</p>
                <p className="mt-2">{copy.databaseRestore.warningBody}</p>
                <p className="mt-2">{copy.databaseRestore.windowDetail(loadedRestoreRetentionDays ?? restoreRetentionDays)}</p>
                {formattedRestoreWindowStart && formattedRestoreWindowEnd ? (
                  <p className="mt-2">{copy.databaseRestore.modal.windowSummary(formattedRestoreWindowStart, formattedRestoreWindowEnd)}</p>
                ) : null}
              </div>

              {isRestorePointsLoading ? (
                <LoadingStateCard label={copy.databaseRestore.modal.loading} />
              ) : restorePointsError ? (
                <div className="rounded-[22px] border border-[#fecdca] bg-[#fef3f2] px-5 py-5">
                  <p className="text-sm font-semibold text-[#b42318]">{restorePointsError}</p>
                  <button
                    type="button"
                    onClick={() => { void loadRestorePoints(); }}
                    className="mt-4 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                  >
                    {copy.databaseRestore.modal.retryAction}
                  </button>
                </div>
              ) : !restoreWindowSummary.earliestTimestamp || !restoreWindowSummary.latestTimestamp ? (
                <div className="rounded-[22px] border border-dashed border-[#d0d5dd] bg-white px-5 py-6 text-center">
                  <p className="text-sm font-semibold text-[#101828]">{copy.databaseRestore.modal.emptyTitle}</p>
                  <p className="mt-2 text-sm text-[#667085]">{copy.databaseRestore.modal.emptyDescription}</p>
                </div>
              ) : (
                <div className="space-y-5 rounded-[22px] border border-[#eaecf0] bg-white px-5 py-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-[18px] border border-[#eaecf0] bg-[#f8fafc] px-4 py-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.databaseRestore.modal.earliestLabel}</p>
                      <p className="mt-2 text-sm font-semibold text-[#101828]">{formattedRestoreWindowStart}</p>
                    </div>
                    <div className="rounded-[18px] border border-[#eaecf0] bg-[#f8fafc] px-4 py-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.databaseRestore.modal.latestLabel}</p>
                      <p className="mt-2 text-sm font-semibold text-[#101828]">{formattedRestoreWindowEnd}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block" htmlFor="database-restore-timestamp">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.databaseRestore.modal.inputLabel}</span>
                      <p className="mt-2 text-sm text-[#667085]">{copy.databaseRestore.modal.inputDescription}</p>
                    </label>
                    <input
                      id="database-restore-timestamp"
                      type="datetime-local"
                      step={1}
                      min={restoreWindowMinInput || undefined}
                      max={restoreWindowMaxInput || undefined}
                      value={selectedRestoreTimestampInput}
                      onChange={event => {
                        setSelectedRestoreTimestampInput(event.target.value);
                        setRestoreDatabaseError("");
                        setRestoreDatabaseSuccess("");
                      }}
                      className="brand-primary-focus-ring mt-3 w-full rounded-xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none"
                    />
                    {normalizedSelectedRestoreTimestamp ? (
                      <p className="mt-3 text-sm text-[#667085]">
                        {copy.databaseRestore.modal.restorePointDetail(
                          formatRestorePointTimestamp(normalizedSelectedRestoreTimestamp, language, restoreDisplayTimezone),
                        )}
                      </p>
                    ) : null}
                  </div>

                  <p className="text-xs text-[#667085]">{copy.databaseRestore.modal.timezoneDetail(restoreDisplayTimezone)}</p>
                </div>
              )}

              {restoreDatabaseError ? (
                <div className="rounded-[18px] border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                  {restoreDatabaseError}
                </div>
              ) : null}

              {restoreDatabaseSuccess ? (
                <div className="rounded-[18px] border border-[#abefc6] bg-[#ecfdf3] px-4 py-3 text-sm text-[#067647]">
                  {restoreDatabaseSuccess}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeRestoreDatabaseModal}
                  className="rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                >
                  {copy.common.cancel}
                </button>
                <button
                  type="button"
                  onClick={() => { void handleRestoreDatabase(); }}
                  disabled={!normalizedSelectedRestoreTimestamp || isRestoringDatabase || isRestorePointsLoading || !restoreWindowSummary.earliestTimestamp || !restoreWindowSummary.latestTimestamp}
                  className="inline-flex items-center justify-center rounded-xl bg-[#b42318] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#912018] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isRestoringDatabase ? copy.databaseRestore.restoringAction : copy.databaseRestore.restoreAction}
                </button>
              </div>
            </div>
          </AnimatedModal>
          </>
          ) : null}

          <AnimatedModal
            open={canManageAdminSettings && isTicketCategoryModalOpen}
            onClose={handleCloseTicketCategoryModal}
            backdropAriaLabel={copy.categories.modal.closeAria}
            overlayClassName="fixed inset-0 z-[90] bg-[#101828]/35 backdrop-blur-[6px]"
            overlayStyle={portalBrandStyle}
            dialogClassName="app-modal-dialog relative max-h-[calc(100vh-4rem)] w-full max-w-3xl overflow-y-auto rounded-[32px] border border-white/70 bg-[#fcfcfd] p-6 shadow-[0_32px_100px_rgba(15,23,42,0.24)] custom-scrollbar"
          >
            <button
              type="button"
              onClick={handleCloseTicketCategoryModal}
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
            >
              ×
            </button>

            <div className="flex flex-wrap items-start justify-between gap-4 pr-16">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{isCreatingTicketCategory ? copy.categories.modal.createEyebrow : copy.categories.modal.editEyebrow}</p>
                <h3 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">
                  {isCreatingTicketCategory ? copy.categories.modal.newTitle : ticketCategoryDraft.name || copy.categories.modal.fallbackTitle}
                </h3>
                <p className="mt-2 max-w-2xl text-sm text-[#667085]">{copy.categories.modal.description}</p>
              </div>
            </div>

            <div className="mt-6 grid gap-4">
              <label className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4">
                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.categories.modal.categoryName}</span>
                <input
                  type="text"
                  value={ticketCategoryDraft.name}
                  onChange={event => setTicketCategoryDraft(current => ({ ...current, name: event.target.value }))}
                  placeholder={copy.categories.modal.categoryNamePlaceholder}
                  className="mt-3 w-full border-none bg-transparent p-0 text-sm font-semibold text-[#101828] outline-none placeholder:text-[#98a2b3]"
                />
              </label>

              <label className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4">
                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.categories.modal.routingGuidance}</span>
                <textarea
                  value={ticketCategoryDraft.description}
                  onChange={event => setTicketCategoryDraft(current => ({ ...current, description: event.target.value }))}
                  placeholder={copy.categories.modal.routingGuidancePlaceholder}
                  className="mt-3 min-h-[140px] w-full resize-none border-none bg-transparent p-0 text-sm text-[#101828] outline-none placeholder:text-[#98a2b3]"
                />
              </label>

              <div className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.categories.modal.customFields}</span>
                    <p className="mt-2 text-sm text-[#667085]">{copy.categories.modal.customFieldsDescription}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddTicketCategoryField()}
                    className="rounded-xl bg-[#f3f4f6] px-4 py-2.5 text-sm font-semibold text-[#344054] transition-colors hover:bg-[#e4e7ec]"
                  >
                    {copy.common.addField}
                  </button>
                </div>

                <div className="mt-4 space-y-4">
                  {ticketCategoryDraft.customFields.length > 0 ? ticketCategoryDraft.customFields.map((field, index) => (
                    <div key={field.id} className="rounded-[20px] border border-[#eaecf0] bg-[#fcfcfd] p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-[#101828]">{copy.categories.modal.fieldLabel(index + 1)}</p>
                        <button
                          type="button"
                          onClick={() => handleDeleteTicketCategoryField(field.id)}
                          className="rounded-xl bg-white px-3 py-2 text-xs font-semibold text-[#b42318] ring-1 ring-[#f3d0cb] transition-colors hover:bg-[#fef3f2]"
                        >
                          {copy.common.remove}
                        </button>
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <label className="rounded-[18px] border border-[#eaecf0] bg-white px-4 py-3 md:col-span-2">
                          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.categories.modal.fieldLabelTitle}</span>
                          <input
                            type="text"
                            value={field.label}
                            onChange={event => handleTicketCategoryFieldChange(field.id, "label", event.target.value)}
                            placeholder={copy.categories.modal.fieldLabelPlaceholder}
                            className="mt-3 w-full border-none bg-transparent p-0 text-sm font-semibold text-[#101828] outline-none placeholder:text-[#98a2b3]"
                          />
                        </label>

                        <label className="rounded-[18px] border border-[#eaecf0] bg-white px-4 py-3">
                          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.categories.modal.fieldType}</span>
                          <select
                            value={field.type}
                            onChange={event => handleTicketCategoryFieldChange(field.id, "type", event.target.value as TicketCategoryCustomFieldType)}
                            className="mt-3 w-full border-none bg-transparent p-0 text-sm font-semibold text-[#101828] outline-none"
                          >
                            <option value="text">{copy.categories.modal.fieldTypes.text}</option>
                            <option value="textarea">{copy.categories.modal.fieldTypes.textarea}</option>
                            <option value="select">{copy.categories.modal.fieldTypes.select}</option>
                            <option value="date">{copy.categories.modal.fieldTypes.date}</option>
                          </select>
                        </label>

                        <label className="flex items-center justify-between rounded-[18px] border border-[#eaecf0] bg-white px-4 py-3">
                          <span>
                            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.categories.modal.required}</span>
                            <p className="mt-1 text-sm text-[#667085]">{copy.categories.modal.requiredDescription}</p>
                          </span>
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={event => handleTicketCategoryFieldChange(field.id, "required", event.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
                          />
                        </label>

                        <label className="rounded-[18px] border border-[#eaecf0] bg-white px-4 py-3 md:col-span-2">
                          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.categories.modal.helpText}</span>
                          <input
                            type="text"
                            value={field.helpText}
                            onChange={event => handleTicketCategoryFieldChange(field.id, "helpText", event.target.value)}
                            placeholder={copy.categories.modal.helpTextPlaceholder}
                            className="mt-3 w-full border-none bg-transparent p-0 text-sm text-[#101828] outline-none placeholder:text-[#98a2b3]"
                          />
                        </label>

                        {field.type === "select" ? (
                          <label className="rounded-[18px] border border-[#eaecf0] bg-white px-4 py-3 md:col-span-2">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.categories.modal.options}</span>
                            <textarea
                              value={field.options.join("\n")}
                              onChange={event => handleTicketCategoryFieldChange(field.id, "options", event.target.value.split(/\n+/).map(option => option.trim()).filter(Boolean))}
                              rows={4}
                              placeholder={copy.categories.modal.optionsPlaceholder}
                              className="mt-3 min-h-[110px] w-full resize-none border-none bg-transparent p-0 text-sm text-[#101828] outline-none placeholder:text-[#98a2b3]"
                            />
                            <p className="mt-2 text-xs text-[#667085]">{copy.categories.modal.optionsHelp}</p>
                          </label>
                        ) : null}
                      </div>
                    </div>
                  )) : (
                    <div className="rounded-[20px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-6 text-center">
                      <p className="text-sm font-semibold text-[#101828]">{copy.categories.modal.noCustomFieldsTitle}</p>
                      <p className="mt-2 text-sm text-[#667085]">{copy.categories.modal.noCustomFieldsDescription}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {ticketCategoryError ? <p className="mt-4 text-sm font-semibold text-[#b42318]">{ticketCategoryError}</p> : null}

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleResetTicketCategoryDraft}
                className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
              >
                {isCreatingTicketCategory ? copy.common.clearForm : copy.common.resetChanges}
              </button>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleCloseTicketCategoryModal}
                  className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]"
                >
                  {copy.common.cancel}
                </button>
                <button
                  type="button"
                  onClick={handleSaveTicketCategory}
                  className="brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all"
                >
                  {isCreatingTicketCategory ? copy.categories.modal.createAction : copy.categories.modal.saveAction}
                </button>
              </div>
            </div>
          </AnimatedModal>

          {section === "workspace" && canManageAdminSettings ? (
          <>
          <SettingsCard
            eyebrow={copy.currency.eyebrow}
            title={copy.currency.title}
            description={copy.currency.description}
          >
            <>
              <div className="rounded-[20px] border border-[#eaecf0] bg-white px-5 py-5 mb-6">
                <label className="block" htmlFor="workspace-timezone">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.currency.timezoneLabel}</span>
                  <p className="mt-2 text-sm text-[#667085]">{copy.currency.timezoneDescription}</p>
                </label>
                <select
                  id="workspace-timezone"
                  value={normalizeWorkspaceTimezone(workspaceSettings.timezone) ?? "UTC"}
                  onChange={event => onUpdateWorkspaceSettings({
                    ...workspaceSettings,
                    timezone: normalizeWorkspaceTimezone(event.target.value) ?? "UTC",
                  })}
                  className="brand-primary-focus-ring mt-3 w-full rounded-xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none"
                >
                  {workspaceTimezoneChoices.map(timezone => (
                    <option key={timezone} value={timezone}>
                      {timezone}
                    </option>
                  ))}
                </select>
                <p className="mt-3 text-sm text-[#667085]">{copy.currency.timezoneHelp}</p>
              </div>

              <div className="rounded-[20px] border border-[#eaecf0] bg-white px-5 py-5">
                <label className="block" htmlFor="workspace-currency">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.currency.label}</span>
                </label>
                <select
                  id="workspace-currency"
                  value={workspaceSettings.currency}
                  onChange={event => onUpdateWorkspaceSettings({
                    ...workspaceSettings,
                    currency: event.target.value,
                  })}
                  className="brand-primary-focus-ring mt-3 w-full rounded-xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none"
                >
                  {currencyOptions.map(option => (
                    <option key={option.label} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="mt-4 rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-4 text-sm text-[#667085]">
                  <p className="font-semibold text-[#101828]">{copy.currency.previewLabel}</p>
                  <p className="mt-2 text-sm font-semibold text-[#101828]">{selectedCurrency.label}</p>
                  <p className="mt-2">{copy.currency.help}</p>
                </div>
              </div>
            </>
          </SettingsCard>

          <SettingsCard
            eyebrow={copy.brand.eyebrow}
            title={copy.brand.title}
            description={copy.brand.description}
          >
            <div className="grid gap-4">
              <div className="rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] px-5 py-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.brand.consoleName}</p>
                <div className="mt-4 grid gap-4">
                  <p className="text-sm text-[#667085]">{copy.brand.consoleNameDetail}</p>
                  <input
                    type="text"
                    value={consoleNameDraft}
                    onChange={event => setConsoleNameDraft(event.target.value)}
                    placeholder={copy.brand.consoleNamePlaceholder}
                    maxLength={40}
                    className="w-full max-w-sm rounded-xl border border-[#d0d5dd] bg-white px-3.5 py-2.5 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-soft)]"
                  />
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => onConsoleNameChange(consoleNameDraft)}
                      disabled={consoleNameDraft.trim() === (branding.consoleName ?? "").trim()}
                      className="brand-primary-button rounded-xl px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {copy.brand.applyConsoleName}
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] px-5 py-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.brand.primaryColor}</p>
                <div className="mt-4 flex flex-wrap items-start gap-5">
                  <div className="flex w-20 shrink-0 flex-col items-center">
                    <label
                      className="relative block h-14 w-20 cursor-pointer overflow-hidden rounded-2xl border border-[#d0d5dd] shadow-inner"
                      style={{ backgroundColor: primaryColorDraft }}
                    >
                      <input
                        type="color"
                        value={primaryColorDraft}
                        onChange={event => setPrimaryColorDraft(event.target.value)}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      />
                    </label>
                    <p className="mt-2 text-center text-sm font-semibold text-[#101828]">{primaryColorDraft.toUpperCase()}</p>
                  </div>
                  <div className="min-w-0 flex-1 pt-1">
                    <p className="text-sm text-[#667085]">{copy.brand.primaryColorDetail}</p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => onPrimaryColorChange(primaryColorDraft)}
                        disabled={primaryColorDraft === branding.primaryColor}
                        className="brand-primary-button rounded-xl px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {copy.brand.applyColor}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] px-5 py-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.brand.logo}</p>
                <div className="relative mt-4">
                  <label className="flex cursor-pointer items-center gap-4 rounded-[18px] border border-[#eaecf0] bg-white px-4 py-4 transition-colors hover:border-[var(--brand-primary)] hover:bg-[var(--brand-primary-soft)]">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      className="hidden"
                      onChange={event => onLogoUpload(event.target.files?.[0] ?? null)}
                    />
                    <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-[#f3f4f6]">
                      {branding.logoDataUrl ? (
                        <img src={branding.logoDataUrl} alt={branding.logoName ?? copy.common.workspaceLogoAlt} className="h-full w-full object-contain p-1.5" />
                      ) : (
                        <span className="text-xl font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">?</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#101828]">{branding.logoName ?? copy.common.noLogoUploadedYet}</p>
                      <p className="mt-1 text-sm text-[#667085]">{branding.logoDataUrl ? copy.common.clickToReplaceLogo : copy.common.clickToChooseLogo}</p>
                    </div>
                  </label>

                  {branding.logoDataUrl ? (
                    <button
                      type="button"
                      aria-label={copy.common.removeLogoAria}
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemoveLogo();
                      }}
                      className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-[#f04438] text-xs font-bold text-white shadow-sm transition-colors hover:bg-[#d92d20]"
                    >
                      ×
                    </button>
                  ) : null}
                </div>

                <p className="mt-3 text-sm text-[#667085]">{copy.brand.logoUploadHelp}</p>
                <p className="mt-1 text-sm text-[#667085]">{copy.brand.logoRatioHelp}</p>
              </div>
            </div>
          </SettingsCard>

          <SettingsCard
            eyebrow={copy.authentication.eyebrow}
            title={copy.authentication.title}
            description={copy.authentication.description}
          >
            <div className="rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] px-5 py-5 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
              <div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.authentication.passwordReset}</p>
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                  <p className="mt-2 text-sm font-semibold text-[#101828]">{workspaceSettings.localPasswordResetEnabled ? copy.common.enabled : copy.common.disabled}</p>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={workspaceSettings.localPasswordResetEnabled}
                    aria-label={copy.authentication.toggleAria}
                    onClick={() => onUpdateWorkspaceSettings({
                      ...workspaceSettings,
                      localPasswordResetEnabled: !workspaceSettings.localPasswordResetEnabled,
                    })}
                    className="inline-flex items-center gap-3 rounded-full bg-[#f3f4f6] px-3 py-2 text-sm font-semibold text-[#344054] transition-colors hover:bg-[#eaecf0]"
                  >
                    <span>{workspaceSettings.localPasswordResetEnabled ? copy.common.on : copy.common.off}</span>
                    <span
                      aria-hidden="true"
                      className={`relative h-7 w-12 rounded-full transition-colors ${workspaceSettings.localPasswordResetEnabled ? "bg-[var(--brand-primary)]" : "bg-[#d0d5dd]"}`}
                    >
                      <span
                        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-[0_2px_8px_rgba(15,23,42,0.18)] transition-all ${workspaceSettings.localPasswordResetEnabled ? "left-6" : "left-1"}`}
                      />
                    </span>
                  </button>
                </div>
                <p className="mt-2 max-w-xl text-sm text-[#667085]">
                  {workspaceSettings.localPasswordResetEnabled
                    ? copy.authentication.enabledDetail
                    : copy.authentication.disabledDetail}
                </p>
                <p className="mt-2 max-w-xl text-sm text-[#667085]">
                  {copy.authentication.expiryDetail}
                </p>
              </div>
            </div>
          </SettingsCard>

          <SettingsCard
            eyebrow={copy.attachmentSharing.eyebrow}
            title={copy.attachmentSharing.title}
            description={copy.attachmentSharing.description}
          >
            <div className="rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] px-5 py-5 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.attachmentSharing.systemLabel}</p>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-[#101828]">{workspaceSettings.publicAttachmentDownloadsEnabled ? copy.common.enabled : copy.common.disabled}</p>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={workspaceSettings.publicAttachmentDownloadsEnabled}
                    aria-label={copy.attachmentSharing.systemToggleAria}
                    onClick={() => onUpdateWorkspaceSettings({
                      ...workspaceSettings,
                      publicAttachmentDownloadsEnabled: !workspaceSettings.publicAttachmentDownloadsEnabled,
                    })}
                    className="inline-flex items-center gap-3 rounded-full bg-[#f3f4f6] px-3 py-2 text-sm font-semibold text-[#344054] transition-colors hover:bg-[#eaecf0]"
                  >
                    <span>{workspaceSettings.publicAttachmentDownloadsEnabled ? copy.common.on : copy.common.off}</span>
                    <span
                      aria-hidden="true"
                      className={`relative h-7 w-12 rounded-full transition-colors ${workspaceSettings.publicAttachmentDownloadsEnabled ? "bg-[var(--brand-primary)]" : "bg-[#d0d5dd]"}`}
                    >
                      <span
                        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-[0_2px_8px_rgba(15,23,42,0.18)] transition-all ${workspaceSettings.publicAttachmentDownloadsEnabled ? "left-6" : "left-1"}`}
                      />
                    </span>
                  </button>
                </div>
                <p className="mt-2 max-w-xl text-sm text-[#667085]">
                  {workspaceSettings.publicAttachmentDownloadsEnabled
                    ? copy.attachmentSharing.systemEnabledDetail
                    : copy.attachmentSharing.systemDisabledDetail}
                </p>
              </div>

              <div className="mt-5 border-t border-[#eaecf0] pt-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.attachmentSharing.overridesLabel}</p>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-[#101828]">{workspaceSettings.attachmentDownloadOverridesEnabled ? copy.common.enabled : copy.common.disabled}</p>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={workspaceSettings.attachmentDownloadOverridesEnabled}
                    aria-label={copy.attachmentSharing.overridesToggleAria}
                    onClick={() => onUpdateWorkspaceSettings({
                      ...workspaceSettings,
                      attachmentDownloadOverridesEnabled: !workspaceSettings.attachmentDownloadOverridesEnabled,
                    })}
                    className="inline-flex items-center gap-3 rounded-full bg-[#f3f4f6] px-3 py-2 text-sm font-semibold text-[#344054] transition-colors hover:bg-[#eaecf0]"
                  >
                    <span>{workspaceSettings.attachmentDownloadOverridesEnabled ? copy.common.on : copy.common.off}</span>
                    <span
                      aria-hidden="true"
                      className={`relative h-7 w-12 rounded-full transition-colors ${workspaceSettings.attachmentDownloadOverridesEnabled ? "bg-[var(--brand-primary)]" : "bg-[#d0d5dd]"}`}
                    >
                      <span
                        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-[0_2px_8px_rgba(15,23,42,0.18)] transition-all ${workspaceSettings.attachmentDownloadOverridesEnabled ? "left-6" : "left-1"}`}
                      />
                    </span>
                  </button>
                </div>
                <p className="mt-2 max-w-xl text-sm text-[#667085]">
                  {workspaceSettings.attachmentDownloadOverridesEnabled
                    ? copy.attachmentSharing.overridesEnabledDetail
                    : copy.attachmentSharing.overridesDisabledDetail}
                </p>
              </div>

              <p className="mt-5 rounded-[18px] border border-[#eaecf0] bg-white px-4 py-3 text-sm text-[#667085]">{copy.attachmentSharing.maxSizeDetail}</p>
            </div>
          </SettingsCard>

          <SettingsCard
            eyebrow={copy.api.eyebrow}
            title={copy.api.title}
            description={copy.api.description}
          >
            <div className="rounded-[22px] border border-[#eaecf0] bg-[#fcfcfd] px-5 py-5 shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
              <div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.api.workspaceApi}</p>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-[#101828]">{workspaceSettings.apiAccessEnabled ? copy.common.enabled : copy.common.disabled}</p>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={workspaceSettings.apiAccessEnabled}
                      aria-label={copy.api.toggleAria}
                      onClick={() => onUpdateWorkspaceSettings({
                        ...workspaceSettings,
                        apiAccessEnabled: !workspaceSettings.apiAccessEnabled,
                      })}
                      className="inline-flex items-center gap-3 rounded-full bg-[#f3f4f6] px-3 py-2 text-sm font-semibold text-[#344054] transition-colors hover:bg-[#eaecf0]"
                    >
                      <span>{workspaceSettings.apiAccessEnabled ? copy.common.on : copy.common.off}</span>
                      <span
                        aria-hidden="true"
                        className={`relative h-7 w-12 rounded-full transition-colors ${workspaceSettings.apiAccessEnabled ? "bg-[var(--brand-primary)]" : "bg-[#d0d5dd]"}`}
                      >
                        <span
                          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-[0_2px_8px_rgba(15,23,42,0.18)] transition-all ${workspaceSettings.apiAccessEnabled ? "left-6" : "left-1"}`}
                        />
                      </span>
                    </button>
                  </div>
                  <p className="mt-2 max-w-xl text-sm text-[#667085]">
                    {workspaceSettings.apiAccessEnabled
                      ? copy.api.enabledDetail
                      : copy.api.disabledDetail}
                  </p>
                  <p className="mt-2 max-w-xl text-sm text-[#667085]">
                    {workspaceSettings.apiKeyExpiryDays
                      ? copy.api.expiryEnabledDetail(formatLocalizedApiKeyExpiryDaysLabel(workspaceSettings.apiKeyExpiryDays, language))
                      : copy.api.expiryDisabledDetail}
                  </p>
                </div>
              </div>

              <div className="mt-5 w-full">
                <label className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]" htmlFor="workspace-api-key-expiry">
                  {copy.api.tenantTokenExpiry}
                </label>
                <select
                  id="workspace-api-key-expiry"
                  value={workspaceSettings.apiKeyExpiryDays === null ? "" : String(workspaceSettings.apiKeyExpiryDays)}
                  onChange={event => onUpdateWorkspaceSettings({
                    ...workspaceSettings,
                    apiKeyExpiryDays: event.target.value ? Number(event.target.value) : null,
                  })}
                  className="brand-primary-focus-ring mt-3 w-full rounded-xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none"
                >
                  {localizedApiKeyExpiryOptions.map(option => (
                    <option key={option.value || "none"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <div className="mt-3 rounded-[18px] border border-[#eaecf0] bg-white px-4 py-4 text-sm text-[#667085]">
                  <p className="font-semibold text-[#101828]">{copy.api.expiryHelpTitle}</p>
                  <p className="mt-2">{copy.api.expiryHelpLineOne}</p>
                  <p className="mt-2">{copy.api.expiryHelpLineTwo}</p>
                </div>
              </div>
            </div>
          </SettingsCard>
          </>
          ) : null}

      </div>
      ) : null}
    </section>
  );
}
